/**
 * AI Ad Factory & UGC Creator Studio Component
 * In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
 * -----------------------------------------------------------------------------------------
 * Elite Purple Glassmorphism Ads & UGC Generation & Tuning Engine
 */

import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Wand2,
  Sparkles,
  Video,
  Play,
  Pause,
  Download,
  Send,
  Zap,
  ArrowUpRight,
  Check,
  RefreshCw,
  Layers,
  Volume2,
  Languages,
  Share2,
  Award,
  Eye,
  Flame,
  Settings,
  Copy,
  Plus,
  Compass,
  AlertCircle,
  BarChart3,
  LineChart,
  Globe,
  HelpCircle,
  Laptop,
  Smartphone,
  FileText,
  Monitor,
  LayoutGrid,
  Trash2,
  ExternalLink,
  Sliders,
  Sparkle
} from "lucide-react";

interface AIAdFactoryUGCCreatorStudioProps {
  triggerToast: (text: string, type?: "success" | "info" | "completed") => void;
  brandUniverse?: any;
}

export function AIAdFactoryUGCCreatorStudio({ triggerToast, brandUniverse }: AIAdFactoryUGCCreatorStudioProps) {
  // Preset defaults if brandUniverse is empty
  const activeBrandName = brandUniverse?.brandName || "NexoraX Premium Systems";
  const activeTone = brandUniverse?.toneOfVoice || "Authoritative, enterprise-grade, Masterly Financial Command";

  // Navigation track
  const workflowSteps = [
    { name: "Product & Offer", key: "product" },
    { name: "Audience", key: "audience" },
    { name: "Hook", key: "hook" },
    { name: "Script", key: "script" },
    { name: "Creative", key: "creative" },
    { name: "UGC Video", key: "ugc" },
    { name: "Ad Variations", key: "variations" },
    { name: "Publish", key: "publish" },
    { name: "Measure", key: "measure" }
  ];

  const [activeWorkflowStep, setActiveWorkflowStep] = useState("product");

  // State for Ad Campaign Builder
  const [productName, setProductName] = useState("NexoraX NexoraX AI Autopilot");
  const [productOffer, setProductOffer] = useState("Direct placement integration locked completely under NexoraX billing records sponsorship.");
  const [targetAudience, setTargetAudience] = useState("Enterprise Recruiters & High-Level Talent Acquisition Managers");
  const [platform, setPlatform] = useState("LinkedIn Sponsored Video");
  const [campaignGoal, setCampaignGoal] = useState("Direct Leads & Institutional Interception");
  const [budgetRange, setBudgetRange] = useState("£5,000 - £20,000 / month");
  const [campaignCta, setCampaignCta] = useState("Schedule Technical Interview With Candidate");

  const [campaignLoading, setCampaignLoading] = useState(false);
  const [generatedCampaign, setGeneratedCampaign] = useState<any>(null);

  // UGC Video Creator State
  const [ugcAvatar, setUgcAvatar] = useState("Eleanor Vance (Executive Assistant)");
  const [ugcStyle, setUgcStyle] = useState("Founder-style video");
  const [ugcVoiceover, setUgcVoiceover] = useState("RP Voice Synthesis - Authoritative");
  const [ugcCaptions, setUgcCaptions] = useState(true);
  const [ugcBrandOverlay, setUgcBrandOverlay] = useState(true);

  const [ugcLoading, setUgcLoading] = useState(false);
  const [generatedUgc, setGeneratedUgc] = useState<any>(null);
  const [isPlayingUgc, setIsPlayingUgc] = useState(false);

  // Hook & Script States
  const [activeHookPlatform, setActiveHookPlatform] = useState("TikTok");
  const [activeScriptStyle, setActiveScriptStyle] = useState("Founder script");
  const [customHookPrompt, setCustomHookPrompt] = useState("Why manual hiring queues are losing your enterprise 35% productivity");
  const [hooksLoading, setHooksLoading] = useState(false);
  const [generatedHooksList, setGeneratedHooksList] = useState<any[]>([]);

  // Ad Creative Generator State
  const [creativeFormat, setCreativeFormat] = useState("Video Ad (9:16 vertical)");
  const [creativeLoading, setCreativeLoading] = useState(false);
  const [generatedCreative, setGeneratedCreative] = useState<any>(null);

  // A/B Variation Lab State
  const [variationLoading, setVariationLoading] = useState(false);
  const [generatedVariations, setGeneratedVariations] = useState<any>(null);

  // Product Shot Studio State
  const [productShotStyle, setProductShotStyle] = useState("Purple Glass branded scene");
  const [shotLoading, setShotLoading] = useState(false);
  const [generatedShots, setGeneratedShots] = useState<any>(null);

  // Multi-Language Campaign Engine State
  const [selectedLanguage, setSelectedLanguage] = useState("English");
  const [langLoading, setLangLoading] = useState(false);
  const [generatedTranslation, setGeneratedTranslation] = useState<any>(null);

  // Publishing State
  const [selectedChannels, setSelectedChannels] = useState<string[]>(["Social Media Automation", "LinkedIn"]);
  const [publishLoading, setPublishLoading] = useState(false);
  const [previewPlatform, setPreviewPlatform] = useState("LinkedIn");

  // Performance learning dynamic live states
  const [bestHook, setBestHookState] = useState(() => localStorage.getItem("nx_best_hook") || "“Save 10 Hours Per Week With AI”");
  const [bestAvatar, setBestAvatarState] = useState(() => localStorage.getItem("nx_best_avatar") || "Eleanor Vance (94% CTR calibration)");
  const [bestDestination, setBestDestinationState] = useState(() => localStorage.getItem("nx_best_destination") || "LinkedIn Sponsored Ad Spot");
  const [bestVideoLength, setBestVideoLengthState] = useState(() => localStorage.getItem("nx_best_vlength") || "15s Vertical Aspect");
  const [bestCta, setBestCtaState] = useState(() => localStorage.getItem("nx_best_cta") || "“Schedule Candidate Technical Interview”");
  const [bestLanguage, setBestLanguageState] = useState(() => localStorage.getItem("nx_best_lang") || "English Baseline & Arabic Localised");
  const [bestAudience, setBestAudienceState] = useState(() => localStorage.getItem("nx_best_audience") || "Venture Recruiters & Private Secretaries");
  const [bestVisual, setBestVisualState] = useState(() => localStorage.getItem("nx_best_visual") || "Obsidian Glass with Glowing Guide lines");

  // Helper sync functions
  const updateBestHook = (val: string) => { setBestHookState(val); localStorage.setItem("nx_best_hook", val); };
  const updateBestAvatar = (val: string) => { setBestAvatarState(val); localStorage.setItem("nx_best_avatar", val); };
  const updateBestDestination = (val: string) => { setBestDestinationState(val); localStorage.setItem("nx_best_destination", val); };
  const updateBestVideoLength = (val: string) => { setBestVideoLengthState(val); localStorage.setItem("nx_best_vlength", val); };
  const updateBestCta = (val: string) => { setBestCtaState(val); localStorage.setItem("nx_best_cta", val); };
  const updateBestLanguage = (val: string) => { setBestLanguageState(val); localStorage.setItem("nx_best_lang", val); };
  const updateBestAudience = (val: string) => { setBestAudienceState(val); localStorage.setItem("nx_best_audience", val); };
  const updateBestVisual = (val: string) => { setBestVisualState(val); localStorage.setItem("nx_best_visual", val); };

  // Helper to increment live tracking KPIs for Nexorax Advanced Console Ecosystem Monitor
  const incrementMetric = (key: string, ratingIncrement: number = 0) => {
    const currentVal = parseInt(localStorage.getItem(key) || "0", 10);
    localStorage.setItem(key, (currentVal + 1).toString());
    
    if (ratingIncrement > 0) {
      const avgCtr = parseFloat(localStorage.getItem("nx_ad_factory_avg_ctr") || "5.42");
      const nextCtr = Math.min(12.0, Math.max(3.0, avgCtr + ratingIncrement));
      localStorage.setItem("nx_ad_factory_avg_ctr", nextCtr.toFixed(2));
    }
  };

  // Logs simulation
  const [telemetryLogs, setTelemetryLogs] = useState<string[]>([
    "[10:48:12] SYSTEM: AI Ad Factory initialised.",
    "[10:48:15] BIOMETRICS: Audio-visual frequency model synchronized with SC887095."
  ]);

  const addLog = (text: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setTelemetryLogs(prev => [`[${timestamp}] ${text}`, ...prev.slice(0, 50)]);
  };

  // Generate handlers
  const handleGenerateCampaign = () => {
    setCampaignLoading(true);
    addLog(`CAMPAIGN: Synthesizing metrics for "${productName}" targeting "${targetAudience}"...`);
    setTimeout(() => {
      setGeneratedCampaign({
        campaignName: `NexoraX Autopilot [${campaignGoal.toUpperCase()}] Campaign`,
        headerCopy: `Direct Institutional Mandate: Zero-Cost ${productName} Deployment`,
        bodyCopy: `The Director of NexoraX Digital Ltd (SC887095) presents a direct billing model. ${productOffer} Access target candidates directly on-grid and bypass traditional agency lead bottlenecks.`,
        suggestedHashtags: ["#NexoraX", "#Client Resource WorkflowStrike", "#AccessToWork", "#AIOps"],
        predictedKPIs: {
          ctr: "5.42%",
          conversionRate: "18.3%",
          estimatedLeads: "1,450",
          gavelConfidenceScore: "99.8%"
        }
      });
      setCampaignLoading(false);
      
      // Live feeding of performance metrics
      updateBestHook(`“Direct Institutional Mandate: Zero-Cost ${productName} Deployment”`);
      updateBestAudience(targetAudience);
      updateBestCta(`“${campaignCta}”`);

      // Real-time KPI feed update
      incrementMetric("nx_total_campaigns_generated", 0.08);

      addLog(`CAMPAIGN: Successfully simulated campaign structure inside NexoraX Resource Library!`);
      triggerToast("AI Ad Campaign synthesized successfully!", "success");
      setActiveWorkflowStep("audience");
    }, 1500);
  };

  const handleGenerateUgc = () => {
    setUgcLoading(true);
    addLog(`UGC_CREATOR: Forging realistic ${ugcStyle} with avatar candidate "${ugcAvatar}"...`);
    setTimeout(() => {
      setGeneratedUgc({
        avatar: ugcAvatar,
        videoUrl: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=1200&auto=format&fit=crop",
        captionsText: "[0:01] Traditional agencies require weeks. [0:04] But with NexoraX direct NexoraX billing records pipelines... [0:08] It is 100% zero-cost under Services Guide SC887095.",
        voiceOverTrack: `${ugcVoiceover} - Fully Rendered`,
        metadata: {
          hasCaptions: ugcCaptions ? "Yes" : "No",
          brandOverlay: ugcBrandOverlay ? "Active (NexoraX watermark)" : "Inactive",
          duration: "15s Vertical Short"
        }
      });
      setUgcLoading(false);

      // Live feeding of performance metrics
      updateBestAvatar(`${ugcAvatar} (94% CTR calibration)`);
      updateBestVideoLength(`15s Vertical Aspect`);

      // Real-time KPI feed update
      incrementMetric("nx_total_ugc_videos_rendered", 0.12);

      addLog(`UGC_CREATOR: Masterfully rendered 15s short video file. Zero digital drift detected.`);
      triggerToast("High-fidelity UGC Video fully compiled and cached!", "success");
      setActiveWorkflowStep("ugc");
    }, 1800);
  };

  const handleGenerateHooksAndScripts = () => {
    setHooksLoading(true);
    addLog(`HOOK_ENGINE: Calculating high-conversion hooks for prompt "${customHookPrompt}"...`);
    setTimeout(() => {
      const presets = {
        TikTok: [
          { hook: "POV: You bypass the recruiter's 20% placement fee completely. Let me show you how.", score: "98.2%", reason: "Disruptive industry reveal" },
          { hook: "Why manual hiring is costing your SaaS up to 10 hours per week and how NexoraX fixes it.", score: "94.5%", reason: "Concrete time-saving angle" },
          { hook: "This is a direct institutional mandate to optimize your AI operations. Watch this.", score: "92.1%", reason: "NexoraX tone curiosity" }
        ],
        YouTube: [
          { hook: "The £150,000 secret Client Resource Workflow agencies don't want you to know regarding Secure Payment Provider Access.", score: "97.4%", reason: "High urgency financial grab" },
          { hook: "Stop wasting institutional capital: How NexoraX Autopilot stabilizes workflows automatically.", score: "95.0%", reason: "Direct executive pain-point" }
        ],
        InstagramStyle: [
          { hook: "Unlocking direct NexoraX candidate pipelines with zero friction. Watch Eleanor Vance.", score: "96.1%", reason: "Biometric avatar visual cue" },
          { hook: "No-Win-No-Fee placements backed by direct-billing structures. Real SaaS scaling stats inside.", score: "91.8%", reason: "Risk reversal" }
        ],
        LinkedIn: [
          { hook: "To the Executive Board: Traditional Client Resource Workflow pipelines delay operations by 45+ days.", score: "99.1%", reason: "High-value professional confrontation" },
          { hook: "How NexoraX Digital (SC887095) is structuring NexoraX workspaces using direct NexoraX billing records grants.", score: "96.9%", reason: "Institutional trust factor" }
        ]
      };

      const selectedList = presets[activeHookPlatform as keyof typeof presets] || presets.LinkedIn;
      setGeneratedHooksList(selectedList);
      setHooksLoading(false);

      // Real-time KPI feed update
      incrementMetric("nx_total_hooks_created", 0.05);

      addLog(`HOOK_ENGINE: Derived highly persuasive hook matrices compiled. Cached.`);
      triggerToast("Stellar high-converting hooks generated!", "success");
      setActiveWorkflowStep("hook");
    }, 1200);
  };

  const handleGenerateCreative = () => {
    setCreativeLoading(true);
    addLog(`CREATIVE_GEN: Compiling visual layouts under style "${brandUniverse?.visualStyle || "Purple Glass"}"...`);
    setTimeout(() => {
      setGeneratedCreative({
        title: `${creativeFormat} Render Node`,
        aspectRatio: creativeFormat.includes("9:16") ? "9:16" : "16:9",
        imagePath: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1200&q=80",
        overlayElements: {
          headerText: "ZERO-FUND PLACEMENT",
          logoText: "NexoraX Premium Systems",
          leadCta: "Call 07359******"
        }
      });
      setCreativeLoading(false);

      // Real-time KPI feed update
      incrementMetric("nx_total_creatives_crafted", 0.06);

      addLog(`CREATIVE_GEN: Compiled dynamic frame layer to asset database.`);
      triggerToast("Ad Creative graphic layout compiled successfully!", "success");
      setActiveWorkflowStep("creative");
    }, 1400);
  };

  const handleGenerateABVariations = () => {
    setVariationLoading(true);
    addLog(`AB_LAB_SIM: Initializing Monte Carlo simulation on 5 separate tracks...`);
    setTimeout(() => {
      setGeneratedVariations({
        hooks: [
          { text: "Bypass standard fee templates. All placement fees fully endowed via Secure Payment Provider.", ctr: "14.1%", score: "94%" },
          { text: "The £150,000 secret traditional agencies hope you never discover.", ctr: "12.8%", score: "98%" },
          { text: "Direct Services Guide from Director Mahmood: Tier-One acquisition.", ctr: "11.2%", score: "89%" }
        ],
        thumbnails: [
          { text: "Gavel strike visual with neon purple highlight lines", ctr: "15.2%", score: "97%" },
          { text: "Eleanor Vance high-contrast violet portrait", ctr: "12.1%", score: "92%" },
          { text: "Secure Payment Provider NexoraX Balance Ledger Display", ctr: "10.4%", score: "85%" }
        ],
        ctas: [
          { text: "Call direct Hotline: 07359******", ctr: "14.9%", score: "99%" },
          { text: "Bypass Client Resource Workflow queues. Schedule Interview", ctr: "13.6%", score: "93%" },
          { text: "Reply code NEXORAX-MIGRATE to secure Candidate", ctr: "11.2%", score: "88%" }
        ],
        captions: [
          { text: "Direct executive routing with 0.8s operational speed standard.", ctr: "13.2%", score: "91%" },
          { text: "Zero agency fees. 100% anchored under NexoraX billing records grant scheme.", ctr: "12.9%", score: "95%" }
        ],
        videoStyles: [
          { text: "Futuristic Purple Glass visual with high-contrast glowing lines", ctr: "16.1%", score: "99%" },
          { text: "Observational raw handheld founder-style selfie video", ctr: "12.3%", score: "89%" }
        ]
      });
      setVariationLoading(false);

      // Real-time KPI feed update
      incrementMetric("nx_total_ab_variations", 0.08);

      addLog(`AB_LAB_SIM: Multi-path variations completed. Leads projection updated to 1,240.`);
      triggerToast("Multivariate A/B variables fully mapped and graded!", "success");
      setActiveWorkflowStep("variations");
    }, 1600);
  };

  const handleGenerateProductShot = () => {
    setShotLoading(true);
    addLog(`SHOT_STUDIO: Laying down physical product scene styled with ${productShotStyle}...`);
    setTimeout(() => {
      setGeneratedShots({
        title: `Product scene: ${productShotStyle}`,
        imageUrl: "https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?q=80&w=1200&auto=format&fit=crop",
        tags: ["Cyberpunk Obsidian", "Glowing Violet Guides", "SC887095 Calibration"]
      });
      setShotLoading(false);

      // Live feeding of performance metrics
      updateBestVisual(`Obsidian Glass with ${productShotStyle}`);

      addLog(`SHOT_STUDIO: High-resolution mockup synthesized. Placed inside Asset Library.`);
      triggerToast("Epic product shot render layer completed!", "success");
    }, 1300);
  };

  const handleGenerateMultiLanguage = () => {
    setLangLoading(true);
    addLog(`LANG_ENGINE: Translating baseline campaign assets to ${selectedLanguage} and building synthesized voice tracks...`);
    setTimeout(() => {
      const localizationData: Record<string, any> = {
        Arabic: {
          script: "باسم الله، العظيم والرحيم. تفويض مؤسسي مباشر: توظيف بلا تكلفة بالكامل عبر مخطط NexoraX billing records.",
          voicePreset: "Abu Dhabi Elite Executive Male Synthesizer",
          captions: "تفويض مباشر | لا رسوم وكالة | احجز المقابلة فوراً",
          status: "Arabic NexoraX Voice Layer Verified"
        },
        Urdu: {
          script: "اللہ کے نام سے جو نہایت مہربان اور رحم والا ہے۔ NexoraX براہ راست بھرتی کا نظام۔ 100٪ مفت منصوبے کے تحت۔",
          voicePreset: "Lahore Node Voice Track Synthesis",
          captions: "براہ راست بھرتی کا حکم نامہ | کوئی فیس نہیں | فوری رابطہ کریں",
          status: "Urdu Voice Anchor calibrated"
        },
        Hindi: {
          script: "नेक्सोराएक्स का सीधा कॉर्पोरेट आदेश। एक्सेस टू वर्क योजना के तहत पूरी तरह से शून्य लागत प्लेसमेंट फी।",
          voicePreset: "Delhi Business Premium Vocal Track",
          captions: "जीरो-कॉस्ट प्लेसमेंट | आज ही इंटरव्यू बुक करें",
          status: "Hindi Localisation Complete"
        },
        Spanish: {
          script: "Mandato corporativo directo de NexoraX Digital: Adquisición de personal de primer nivel a costo cero.",
          voicePreset: "Madrid Executive Dialect Track",
          captions: "Placements 100% sin costo bajo el esquema NexoraX billing records.",
          status: "Castilian Vocal layer generated"
        },
        English: {
          script: "In the name of Allah. Direct Services Guide from the Office of the Director: Zero-Cost placement.",
          voicePreset: "London RP Authoritative Vocal Sync",
          captions: "No agency fees | Direct billing to NexoraX billing records.",
          status: "English Baseline Active"
        },
        French: {
          script: "Mandat institutionnel direct: Intégration à coût zéro via les fonds NexoraX billing records.",
          voicePreset: "Paris Executive Female Vocal Track",
          captions: "Recrutement direct sans intermédiaire.",
          status: "French Localization Rendered"
        },
        German: {
          script: "Direktes Firmenmandat von Director Mahmood: Null-Kosten Stellenbesetzung via NexoraX billing records.",
          voicePreset: "Frankfurt Institutional Anchor Voice",
          captions: "Keine Vermittlungsgebühren. Jetzt prüfen.",
          status: "German Translation Active"
        }
      };

      setGeneratedTranslation(localizationData[selectedLanguage] || localizationData.English);
      setLangLoading(false);

      // Live feeding of performance metrics
      updateBestLanguage(`English Baseline & ${selectedLanguage} Localised`);

      addLog(`LANG_ENGINE: Asset translation for ${selectedLanguage} finalized. Ready for dispatch.`);
      triggerToast(`Campaign localized successfully to ${selectedLanguage}!`, "success");
    }, 1400);
  };

  const handlePublishCampaign = () => {
    setPublishLoading(true);
    addLog(`PUBLISHING: Connecting endpoints, pushing video segments, and replicating metadata...`);
    setTimeout(() => {
      setPublishLoading(false);

      // Real-time KPI feed update - increment published campaigns and update dominant channel
      incrementMetric("nx_total_campaigns_published", 0.15);
      localStorage.setItem("nx_last_published_platform", previewPlatform);

      addLog(`PUBLISHING: Dynamic export fully accepted by social platforms queue!`);
      triggerToast("NexoraX campaign pack disseminated to all active channels!", "success");
      setActiveWorkflowStep("measure");
    }, 1800);
  };

  return (
    <div id="ai-ad-factory-creator-studio-node" className="space-y-8 animate-fadeIn text-left">
      {/* HEADER SECTION */}
      <div className="relative p-6 md:p-8 rounded-3xl bg-gradient-to-br from-[#12072b]/95 via-[#0b0518]/90 to-black border border-purple-500/20 overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 p-4 flex gap-2">
          <span className="text-[9px] font-mono bg-purple-900/40 text-purple-300 px-2.5 py-1 rounded-full border border-purple-500/30 uppercase font-black tracking-widest">
            NODE-07.5 AD COMMAND
          </span>
          <span className="text-[9px] font-mono bg-cyan-900/40 text-[#00e5ff] px-2.5 py-1 rounded-full border border-cyan-500/30 uppercase font-bold animate-pulse">
            HIGH-CONVERSION v9
          </span>
        </div>

        <div className="max-w-3xl space-y-2">
          <span className="text-[10px] font-mono text-purple-400 uppercase tracking-widest font-black block">
            NEXORAX NEXORAX REVENUE GENERATOR
          </span>
          <h2 className="text-2xl md:text-3.5xl font-sans font-black text-white tracking-tight leading-none">
            AI Ad Factory & UGC Creator Studio
          </h2>
          <p className="text-zinc-400 text-xs md:text-sm leading-relaxed max-w-2xl pt-1">
            Turn generated assets and biometrics into absolute conversion tools. Synthesize, edit, test, and disseminate enterprise-grade campaigns designed to satisfy Resource £150,000 and direct access limits.
          </p>
        </div>

        {/* WORKFLOW PIPELINE TRACK */}
        <div className="mt-6 pt-6 border-t border-white/5 overflow-x-auto scrollbar-none">
          <div className="flex items-center gap-2 min-w-max pb-1">
            {workflowSteps.map((step, idx) => {
              const isActive = activeWorkflowStep === step.key;
              return (
                <div key={step.key} className="flex items-center">
                  <button
                    onClick={() => {
                      setActiveWorkflowStep(step.key);
                      addLog(`VIEW_PORT: Navigation layer directed to step: ${step.name.toUpperCase()}`);
                    }}
                    className={`px-3 py-1.5 rounded-xl font-mono text-[9.5px] uppercase tracking-wider font-bold border transition-all cursor-pointer ${
                      isActive
                        ? "bg-gradient-to-r from-purple-900/30 to-cyan-900/30 text-white border-purple-500/50 shadow-[0_0_15px_rgba(168,85,247,0.15)]"
                        : "bg-black/40 border-transparent text-zinc-500 hover:text-zinc-300 hover:border-white/5"
                    }`}
                  >
                    {idx + 1}. {step.name}
                  </button>
                  {idx < workflowSteps.length - 1 && (
                    <span className="text-zinc-700 text-xs mx-1">→</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* TOP KPI PANEL */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: "Active Campaigns", val: "12", sub: "Live Channels", icon: <Layers size={13} className="text-purple-400" /> },
          { label: "UGC Videos Generated", val: "48", sub: "Verified Actors", icon: <Video size={13} className="text-[#00e5ff]" /> },
          { label: "Ad Variations", val: "120", sub: "A/B Matrices", icon: <Sliders size={13} className="text-pink-400" /> },
          { label: "Predicted Avg CTR", val: "4.8%", sub: "+12.4% vs industry", icon: <Flame size={13} className="text-yellow-400" /> },
          { label: "Lead Forecast", val: "1,240 /mo", sub: "High Confidence", icon: <Zap size={13} className="text-emerald-400" /> },
          { label: "Best Performing Hook", val: "“Save 10 hrs per week”", sub: "14.1% Live CTR", icon: <Award size={13} className="text-amber-400" /> }
        ].map((kpi, idx) => (
          <div
            key={idx}
            className="p-4 rounded-2xl bg-gradient-to-b from-[#13072b]/50 to-black/80 border border-purple-500/10 shadow-lg space-y-1.5 hover:border-purple-500/30 transition-all"
          >
            <div className="flex justify-between items-center">
              <span className="text-[9.5px] font-mono text-zinc-450 uppercase font-medium">{kpi.label}</span>
              {kpi.icon}
            </div>
            <div className="text-lg font-mono font-black text-white leading-none tracking-tight">{kpi.val}</div>
            <div className="text-[8.5px] font-mono text-zinc-500 uppercase">{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* MAIN SYSTEM AREA */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* INTERACTIVE CONTROLS COLUMN (LEFT 2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* SECTION 1: AD CAMPAIGN BUILDER */}
          <div className="p-6 rounded-3xl bg-black/60 backdrop-blur-xl border border-purple-500/15 shadow-xl space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-white/5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                <h3 className="text-sm font-mono font-black text-white uppercase tracking-wider">
                  1. Ad Campaign Builder
                </h3>
              </div>
              <span className="text-[8px] font-mono bg-purple-950 px-2 py-0.5 rounded text-purple-300">SYSTEM-AIOPS</span>
            </div>

            {/* Campaign Template Selector */}
            <div className="space-y-2 pb-3 border-b border-purple-500/10">
              <span className="text-zinc-400 uppercase font-bold text-[8.5px] font-mono block">★ Load Premium Campaign Presets</span>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  {
                    name: "Elite Talent Acquisition",
                    prod: "NexoraX Principal Private Secretary Search",
                    audience: "Executive Placement Agencies & Client Resource Workflow Partners",
                    offer: "Zero placement fee scheme. All costs fully covered via direct NexoraX billing records billing (NI NH******B) under SC887095.",
                    goal: "NexoraX Secure Invoice Register candidate interview scheduling",
                    cta: "Call direct hotline 07359****** or schedule interview",
                    budget: "£5,000 / month (Secure Payment Provider Subsidised)",
                    platform: "LinkedIn Sponsored Video"
                  },
                  {
                    name: "Resource £150,000 resource Push",
                    prod: "NexoraX Resource NexoraX Infrastructure",
                    audience: "High-Net-Worth Enterprise Transformation Officers",
                    offer: "Secure direct-to-bare-metal licensing. Complete alignment with Resource £150,000 resource parameters and Secure Payment Provider sort code 04-**-**.",
                    goal: "Secure direct operations licensing",
                    cta: "Unlock NexoraX Resource Library access and route SecurePayment settlements",
                    budget: "£20,000 / month (Endowed)",
                    platform: "Multi-Platform Omnipresent"
                  },
                  {
                    name: "0.8s Telemetry Autopilot",
                    prod: "NexoraX Autonomous Operations Node",
                    audience: "NexoraX Systems Architects & Tech Directors",
                    offer: "Zero-latency core shunting grid running defensive shields against duplicated clones with automatic Google Drive backups.",
                    goal: "Autonomous Node registration",
                    cta: "Deploy and active node telemetry loggers",
                    budget: "£10,000 / month (NexoraX)",
                    platform: "YouTube Cinematic Preroll"
                  }
                ].map((tpl) => (
                  <button
                    key={tpl.name}
                    type="button"
                    onClick={() => {
                      setProductName(tpl.prod);
                      setTargetAudience(tpl.audience);
                      setProductOffer(tpl.offer);
                      setCampaignGoal(tpl.goal);
                      setCampaignCta(tpl.cta);
                      setBudgetRange(tpl.budget);
                      setPlatform(tpl.platform);
                      triggerToast(`Template loaded: ${tpl.name}`, "success");
                      addLog(`TEMPLATES: Converted campaign profile to "${tpl.name.toUpperCase()}" preset.`);
                    }}
                    className="p-2 ml-0 rounded-xl border border-purple-500/20 bg-[#0e041d]/80 text-left text-[9.5px] font-mono hover:border-pink-500/50 hover:bg-purple-950/20 text-zinc-300 transition-all cursor-pointer group"
                  >
                    <span className="text-[#00e5ff] font-bold block group-hover:text-pink-400">⚡ {tpl.name}</span>
                    <span className="text-[8.5px] text-zinc-500 line-clamp-1 block mt-0.5">{tpl.prod}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* DYNAMIC SIDE-BY-SIDE GRID COLLABORATOR */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
              
              {/* Form Input Controllers (7/12 parts) */}
              <div className="xl:col-span-7 space-y-4 text-left">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
                  <div className="space-y-1.5">
                    <label className="text-zinc-400 uppercase font-bold text-[9px]">Product / Service Name</label>
                    <input
                      type="text"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      className="w-full bg-black/80 border border-[#4c1d95]/40 p-2.5 rounded-xl text-white focus:outline-none focus:border-purple-500 text-[11px]"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-zinc-400 uppercase font-bold text-[9px]">Target Audience</label>
                    <input
                      type="text"
                      value={targetAudience}
                      onChange={(e) => setTargetAudience(e.target.value)}
                      className="w-full bg-black/80 border border-[#4c1d95]/40 p-2.5 rounded-xl text-white focus:outline-none focus:border-purple-500 text-[11px]"
                    />
                  </div>

                  <div className="space-y-1.5 md:col-span-2">
                    <label className="text-zinc-400 uppercase font-bold text-[9px]">Core Corporate Offer Details</label>
                    <textarea
                      rows={2}
                      value={productOffer}
                      onChange={(e) => setProductOffer(e.target.value)}
                      className="w-full bg-black/80 border border-[#4c1d95]/40 p-2.5 rounded-xl text-white focus:outline-none focus:border-purple-500 text-[11px] resize-none leading-relaxed"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-zinc-400 uppercase font-bold text-[9px]">Platform Destination</label>
                    <select
                      value={platform}
                      onChange={(e) => setPlatform(e.target.value)}
                      className="w-full bg-black/80 border border-[#4c1d95]/40 p-2.5 rounded-xl text-zinc-300 focus:outline-none focus:border-[#00e5ff] text-[11px] cursor-pointer"
                    >
                      <option>LinkedIn Sponsored Video</option>
                      <option>YouTube Cinematic Preroll</option>
                      <option>TikTok In-Feed Brand Takeover</option>
                      <option>Instagram Reels Immersive</option>
                      <option>Facebook Feed Carousel</option>
                      <option>Multi-Platform Omnipresent</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-zinc-400 uppercase font-bold text-[9px]">SaaS Campaign Goal</label>
                    <input
                      type="text"
                      value={campaignGoal}
                      onChange={(e) => setCampaignGoal(e.target.value)}
                      className="w-full bg-black/80 border border-[#4c1d95]/40 p-2.5 rounded-xl text-white focus:outline-none focus:border-purple-500 text-[11px]"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-zinc-400 uppercase font-bold text-[9px]">Budget Envelope</label>
                    <input
                      type="text"
                      value={budgetRange}
                      onChange={(e) => setBudgetRange(e.target.value)}
                      className="w-full bg-black/80 border border-[#4c1d95]/40 p-2.5 rounded-xl text-white focus:outline-none focus:border-purple-500 text-[11px]"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-zinc-400 uppercase font-bold text-[9px]">Instant Call to Action (CTA)</label>
                    <input
                      type="text"
                      value={campaignCta}
                      onChange={(e) => setCampaignCta(e.target.value)}
                      className="w-full bg-black/80 border border-[#4c1d95]/40 p-2.5 rounded-xl text-[#00e5ff] focus:outline-none focus:border-[#00e5ff] text-[11px]"
                    />
                  </div>
                </div>

                <button
                  onClick={handleGenerateCampaign}
                  disabled={campaignLoading}
                  className="w-full py-3 mt-4 bg-gradient-to-r from-purple-500 to-pink-500 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all hover:opacity-95 disabled:opacity-50 cursor-pointer shadow-lg scroll-mt-2"
                >
                  {campaignLoading ? "Generating Campaign Parameters..." : "Generate Ad Campaign"}
                </button>
              </div>

              {/* REAL-TIME CREATIVE PREVIEW PANE (5/12 parts) */}
              <div className="xl:col-span-5 p-4 rounded-2xl bg-[#04010a] border border-purple-500/20 flex flex-col justify-between text-left relative overflow-hidden">
                <div className="absolute top-0 right-0 py-1.5 px-3 bg-purple-500/10 text-purple-400 font-mono text-[8px] uppercase tracking-wider rounded-bl-xl border-l border-b border-white/5 font-bold">
                  Live Feed Preview
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-1.5 pb-2 border-b border-white/5">
                    <span className="text-purple-400 font-mono text-[10px] font-bold block uppercase tracking-wide">
                      ⚡ REAL-TIME INSTANT FEED AD
                    </span>
                  </div>

                  {/* Feed mock renderer */}
                  <div className="space-y-2 text-zinc-300 font-sans text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#fc42c1] to-[#00e5ff] flex items-center justify-center text-[9px] text-black font-extrabold font-mono uppercase">
                        NX
                      </div>
                      <div>
                        <span className="text-white font-black block text-[10px] uppercase tracking-wide">{activeBrandName}</span>
                        <span className="text-zinc-500 text-[8px] block font-mono">Real-time Draft • Promoted • {platform}</span>
                      </div>
                    </div>

                    <p className="text-zinc-200 text-[10.5px] leading-relaxed line-clamp-4 font-sans italic p-2 bg-black/40 rounded-xl border border-white/5">
                      "To {targetAudience || "our enterprise networks"}: Integrating {productName || "NexoraX Framework"} leads to supreme optimization metrics. {productOffer || "Configuration details synchronizing..."}"
                    </p>

                    <div className="rounded-xl overflow-hidden aspect-video bg-zinc-950 border border-white/10 relative flex flex-col justify-end p-3">
                      <img
                        src="https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=400&q=85"
                        alt="Workspace Pre-render Preview"
                        className="absolute inset-0 w-full h-full object-cover opacity-35"
                        referrerPolicy="no-referrer"
                      />
                      <div className="relative z-10 p-2 bg-black/80 rounded border border-[#fc42c1]/20 max-w-xs self-start backdrop-blur-sm">
                        <span className="text-pink-400 font-mono text-[6.5px] uppercase block font-black">Ad Campaign Hook Concept</span>
                        <p className="text-white font-bold leading-tight text-[8px] uppercase font-mono truncate">
                          {campaignGoal || "Direct Leads Sweep"}
                        </p>
                      </div>
                    </div>

                    <div className="bg-zinc-950 p-2 rounded-xl border border-[#00e5ff]/10 flex justify-between items-center text-[8.5px] font-mono gap-1">
                      <div className="min-w-0">
                        <span className="text-zinc-500 uppercase block text-[7px]">CTA PRESET</span>
                        <span className="text-emerald-400 font-black truncate block">{campaignCta || "Schedule Candidate Technical Interview"}</span>
                      </div>
                      <span className="text-zinc-400 bg-white/5 border border-white/10 px-2 py-0.5 rounded uppercase font-bold text-[7.5px]">
                        {budgetRange || "£5,000"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="text-[7.5px] font-mono text-zinc-500 mt-2 border-t border-white/5 pt-2 flex justify-between items-center bg-black/10 p-1.5 rounded-lg">
                  <span>LATENCY SPEED: 0.8s SLA SAFE</span>
                  <span className="text-[#00e5ff] font-semibold uppercase">✓ ALL PARAMETERS SYNCED</span>
                </div>
              </div>

            </div>

            {/* Simulated Live Pitch Output Block */}
            <AnimatePresence>
              {generatedCampaign && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="p-4 rounded-2xl bg-[#090317] border border-purple-500/30 space-y-3"
                >
                  <div className="flex justify-between items-center text-[10.5px] font-mono">
                    <span className="text-[#00e5ff] font-bold uppercase">✓ SIMULATED OUTPOST DEPLOYED</span>
                    <span className="text-zinc-500">Predicted Yield Ready</span>
                  </div>

                  <div className="space-y-2 text-xs font-mono bg-black/80 p-3.5 rounded-xl border border-white/5">
                    <div>
                      <span className="text-[9px] text-zinc-500 block uppercase font-bold">Campaign Header Title</span>
                      <p className="text-white font-bold">{generatedCampaign.headerCopy}</p>
                    </div>
                    <div className="border-t border-white/5 pt-2 mt-2">
                       <span className="text-[9px] text-zinc-500 block uppercase font-bold">Ad Creative Copywriting Body</span>
                      <p className="text-zinc-300 text-[11px] leading-relaxed mt-1">{generatedCampaign.bodyCopy}</p>
                    </div>
                    <div className="flex gap-2 flex-wrap pt-2">
                      {generatedCampaign.suggestedHashtags.map((h: string, i: number) => (
                        <span key={i} className="text-[#00e5ff] text-[10px]">{h}</span>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-center pt-1 font-mono uppercase text-[9px]">
                    <div className="p-2 bg-black border border-white/5 rounded-xl">
                      <span className="text-zinc-500 block">Estimated CTR</span>
                      <span className="text-emerald-400 font-black block mt-0.5">{generatedCampaign.predictedKPIs.ctr}</span>
                    </div>
                    <div className="p-2 bg-black border border-white/5 rounded-xl">
                      <span className="text-zinc-500 block">Est Conv Rate</span>
                      <span className="text-emerald-400 font-black block mt-0.5">{generatedCampaign.predictedKPIs.conversionRate}</span>
                    </div>
                    <div className="p-2 bg-black border border-white/5 rounded-xl">
                      <span className="text-zinc-500 block">Leads Proj</span>
                      <span className="text-yellow-400 font-black block mt-0.5">{generatedCampaign.predictedKPIs.estimatedLeads} /mo</span>
                    </div>
                    <div className="p-2 bg-black border border-white/5 rounded-xl">
                      <span className="text-zinc-500 block">Audit Safety</span>
                      <span className="text-purple-400 font-black block mt-0.5">{generatedCampaign.predictedKPIs.gavelConfidenceScore}</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* SECTION 2: UGC VIDEO CREATOR */}
          <div className="p-6 rounded-3xl bg-black/60 backdrop-blur-xl border border-purple-500/15 shadow-xl space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-white/5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#00e5ff] animate-pulse" />
                <h3 className="text-sm font-mono font-black text-white uppercase tracking-wider">
                  2. UGC Video Creator
                </h3>
              </div>
              <span className="text-[8px] font-mono bg-cyan-950 px-2 py-0.5 rounded text-cyan-300">VIRTUAL AVATARS</span>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
              
              {/* Form Input Controllers (7/12 parts) */}
              <div className="xl:col-span-7 space-y-4 text-left">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
                  <div className="space-y-1.5">
                    <label className="text-zinc-400 uppercase font-bold text-[9px]">Select AI Lead Actor</label>
                    <select
                      value={ugcAvatar}
                      onChange={(e) => {
                        setUgcAvatar(e.target.value);
                        triggerToast(`Lead Actor selected: ${e.target.value}`, "info");
                      }}
                      className="w-full bg-black/80 border border-cyan-500/20 p-2.5 rounded-xl text-zinc-300 focus:outline-none focus:border-cyan-500 text-[11px] cursor-pointer"
                    >
                      <option>Eleanor Vance (Executive Assistant)</option>
                      <option>NexoraX Commander VIP Model</option>
                      <option>Communications Associate (Communications Director)</option>
                      <option>Staff Coordinator (Operations Node Specialist)</option>
                      <option>Claire (Premium Private Office Lead)</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-zinc-400 uppercase font-bold text-[9px]">UGC Narrative Frame style</label>
                    <select
                      value={ugcStyle}
                      onChange={(e) => {
                        setUgcStyle(e.target.value);
                        triggerToast(`Style updated: ${e.target.value}`, "info");
                      }}
                      className="w-full bg-black/80 border border-cyan-500/20 p-2.5 rounded-xl text-zinc-300 focus:outline-none focus:border-cyan-500 text-[11px] cursor-pointer"
                    >
                      <option>Founder-style video</option>
                      <option>Customer testimonial</option>
                      <option>Product review</option>
                      <option>Problem / solution</option>
                      <option>Before / after comparison</option>
                      <option>Biometric interactive walkthrough</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-zinc-400 uppercase font-bold text-[9px]">Dialect & Voiceover Track</label>
                    <select
                      value={ugcVoiceover}
                      onChange={(e) => {
                        setUgcVoiceover(e.target.value);
                        triggerToast(`Voice synthesis: ${e.target.value}`, "info");
                      }}
                      className="w-full bg-black/80 border border-cyan-500/20 p-2.5 rounded-xl text-zinc-300 focus:outline-none focus:border-cyan-500 text-[11px] cursor-pointer"
                    >
                      <option>RP Voice Synthesis - Authoritative</option>
                      <option>Premium British Executive Cadet Accent</option>
                      <option>Urdu Node Synchronized Anchor</option>
                      <option>Deep Cinematic Narration Frequency</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2 justify-between bg-black/40 border border-white/5 p-3 rounded-xl">
                      <span className="text-zinc-400 uppercase text-[8.5px] font-bold">Localised Captions</span>
                      <input
                        type="checkbox"
                        checked={ugcCaptions}
                        onChange={(e) => {
                          setUgcCaptions(e.target.checked);
                          triggerToast(`Captions toggled: ${e.target.checked ? "ON" : "OFF"}`, "info");
                        }}
                        className="w-4 h-4 accent-purple-500 cursor-pointer"
                      />
                    </div>

                    <div className="flex items-center gap-2 justify-between bg-black/40 border border-white/5 p-3 rounded-xl">
                      <span className="text-zinc-400 uppercase text-[8.5px] font-bold">Brand Overlay Watermark</span>
                      <input
                        type="checkbox"
                        checked={ugcBrandOverlay}
                        onChange={(e) => {
                          setUgcBrandOverlay(e.target.checked);
                          triggerToast(`Watermark toggled ${e.target.checked ? "ON" : "OFF"}`, "info");
                        }}
                        className="w-4 h-4 accent-purple-500 cursor-pointer"
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleGenerateUgc}
                  disabled={ugcLoading}
                  className="w-full py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all hover:opacity-95 disabled:opacity-50 cursor-pointer shadow-lg"
                >
                  {ugcLoading ? "Synthesizing Video Frame Data..." : "Generate UGC Video"}
                </button>
              </div>

              {/* REAL-TIME CREATIVE PREVIEW PANE (5/12 parts) */}
              <div className="xl:col-span-5 p-4 rounded-2xl bg-[#03010b]/95 border border-cyan-500/20 flex flex-col justify-between text-left relative overflow-hidden">
                <div className="absolute top-0 right-0 py-1.5 px-3 bg-cyan-500/10 text-cyan-400 font-mono text-[8px] uppercase tracking-wider rounded-bl-xl border-l border-b border-white/5 font-bold">
                  UGC Player Preview
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-1.5 pb-2 border-b border-white/5">
                    <span className="text-cyan-400 font-mono text-[10px] font-bold block uppercase tracking-wide">
                      ⚡ REAL-TIME INSTANT UGC
                    </span>
                  </div>

                  {/* Vertical Video Frame Simulation */}
                  <div className="relative aspect-[9/16] bg-zinc-950 rounded-xl overflow-hidden flex flex-col justify-between p-3.5 h-64 mx-auto w-40 border border-cyan-500/25 shadow-2s shadow-cyan-500/10">
                    <img
                      src={
                        ugcAvatar.includes("Eleanor")
                          ? "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=400&auto=format&fit=crop"
                          : ugcAvatar.includes("Communications Associate")
                          ? "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=400&auto=format&fit=crop"
                          : ugcAvatar.includes("Staff Coordinator")
                          ? "https://images.unsplash.com/photo-1629425733761-caae3b5f2e50?q=80&w=400&auto=format&fit=crop"
                          : ugcAvatar.includes("Claire")
                          ? "https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=400&auto=format&fit=crop"
                          : "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=400&auto=format&fit=crop"
                      }
                      alt="UGC Selected Actor Pre-render"
                      className="absolute inset-0 w-full h-full object-cover opacity-45"
                      referrerPolicy="no-referrer"
                    />

                    <div className="relative z-10 flex justify-between items-center text-[6px] font-mono text-zinc-350 bg-black/60 px-1 py-0.5 rounded">
                      <span className="text-pink-400">@nexorax</span>
                      <span className="font-extrabold text-[5px]">ACTOR PREVIEW</span>
                    </div>

                    <div className="relative z-10 space-y-1 self-end w-full">
                      {/* Watermark Overlay in real-time */}
                      {ugcBrandOverlay && (
                        <div className="bg-purple-950/75 border border-purple-500/30 text-[5px] text-zinc-300 font-mono text-center p-0.5 rounded tracking-wide uppercase">
                          SC887095 - SECURE WATERMARK
                        </div>
                      )}

                      <p className="text-[7.5px] font-bold text-cyan-300 font-sans tracking-wide truncate bg-black/40 px-1 rounded-sm">
                        {ugcAvatar.split(" ")[0]} Vance
                      </p>

                      {/* Captions Overlay in real-time */}
                      {ugcCaptions && (
                        <div className="bg-black/95 p-1 rounded text-[5px] font-mono text-zinc-300 leading-tight border border-white/5 uppercase">
                          [Voice: {ugcVoiceover.split(" ")[0]}] "Secure Payment Provider Direct NH******B billing enabled under {ugcStyle.split(" ")[0]}."
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-[7.5px] font-mono text-zinc-500 mt-2 border-t border-white/5 pt-1.5 flex justify-between items-center bg-black/10 p-1 rounded-lg">
                  <span>VOICEOVER DIALECT OK</span>
                  <span className="text-[#00e5ff] font-semibold uppercase">{ugcStyle.split(" ")[0]} READY</span>
                </div>
              </div>

            </div>

            {/* Generated UGC player preview */}
            <AnimatePresence>
              {generatedUgc && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-2xl bg-[#030109] border border-cyan-500/30"
                >
                  {/* Fake Interactive video player */}
                  <div className="relative rounded-xl overflow-hidden border border-white/10 aspect-[9/16] bg-zinc-950 flex flex-col justify-between p-3 h-64 md:h-80 mx-auto w-48">
                    <img
                      src={generatedUgc.videoUrl}
                      alt="UGC Render Frame"
                      className="absolute inset-0 w-full h-full object-cover opacity-60"
                      referrerPolicy="no-referrer"
                    />

                    {/* Telemetry frame indicators */}
                    <div className="relative z-10 flex justify-between items-center text-[7.5px] font-mono uppercase bg-black/40 px-1.5 py-0.5 rounded text-zinc-400">
                      <span className="text-cyan-400 font-bold">LIVE STAGE</span>
                      <span className="text-purple-400">1080P v9</span>
                    </div>

                    {/* Simulation play/pause state */}
                    <div className="relative z-10 flex justify-center items-center py-4 my-auto">
                      <button
                        onClick={() => setIsPlayingUgc(!isPlayingUgc)}
                        className="w-10 h-10 rounded-full bg-black/80 hover:bg-black border border-cyan-400/50 flex items-center justify-center text-[#00e5ff] cursor-pointer"
                      >
                        {isPlayingUgc ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
                      </button>
                    </div>

                    {/* Caption render inside player overlay */}
                    {ugcCaptions && (
                      <div className="relative z-10 bg-black/80 p-2 rounded-lg text-[8px] font-mono text-center text-yellow-300 border border-yellow-500/20 leading-relaxed shadow-lg">
                        {isPlayingUgc ? "[Playing Voicetrack Sync...] \"100% zero-cost placements authorized under Secure Payment Provider grants.\"" : "Click Play to review subtitle overlay cadence"}
                      </div>
                    )}
                  </div>

                  {/* Settings details */}
                  <div className="md:col-span-2 space-y-3 font-mono text-xs">
                    <div className="space-y-1.5">
                      <span className="text-[#00e5ff] font-bold text-[10px] uppercase tracking-wider block">★ SYSTEM GENERATION DIAGNOSTIC</span>
                      <div className="bg-black/95 p-3 rounded-xl border border-white/5 space-y-2 text-[11px] text-zinc-300 font-sans">
                        <p><strong className="font-mono text-[9px] text-zinc-500 block">ACTOR IDENTIFIED:</strong> {generatedUgc.avatar}</p>
                        <p className="pt-1.5 border-t border-white/5"><strong className="font-mono text-[9px] text-zinc-500 block">CAPTIONS LOG:</strong> {generatedUgc.captionsText}</p>
                        <p className="pt-1.5 border-t border-white/5"><strong className="font-mono text-[9px] text-zinc-500 block font-bold">ROUTING TUNER:</strong> {generatedUgc.voiceOverTrack}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 uppercase text-[7.5px] text-center text-zinc-500">
                      <div className="p-1 px-1.5 bg-zinc-950 border border-white/5 rounded-lg text-emerald-400">
                        <span className="block font-sans">Captions Status</span>
                        <strong>{generatedUgc.metadata.hasCaptions}</strong>
                      </div>
                      <div className="p-1 px-1.5 bg-zinc-950 border border-white/5 rounded-lg text-[#00e5ff]">
                        <span className="block font-sans">Watermark</span>
                        <strong>{generatedUgc.metadata.brandOverlay}</strong>
                      </div>
                      <div className="p-1 px-1.5 bg-zinc-950 border border-white/10 rounded-lg text-purple-400">
                        <span className="block font-sans">Frame Ratio</span>
                        <strong>{generatedUgc.metadata.duration}</strong>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          triggerToast("Downloading optimized 1080P vertical MP4...", "success");
                          addLog("ASSETS: Downloaded UGC clip file: Eleanor_Direct_Handshake_9_16.mp4");
                        }}
                        className="flex-1 py-2 bg-purple-950/50 text-white hover:bg-purple-900/60 border border-purple-500/30 font-bold uppercase text-[9px] rounded-lg tracking-widest cursor-pointer text-center"
                      >
                        Download Render Clip
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* SECTION 3: HOOK & SCRIPT GENERATOR */}
          <div className="p-6 rounded-3xl bg-black/60 backdrop-blur-xl border border-purple-500/15 shadow-xl space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-white/5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
                <h3 className="text-sm font-mono font-black text-white uppercase tracking-wider">
                  3. Hook & Script Generator
                </h3>
              </div>
              <span className="text-[8px] font-mono bg-pink-950 px-2 py-0.5 rounded text-pink-300">VIRAL PARAMETERS</span>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
              
              {/* Form Input Controllers (7/12 parts) */}
              <div className="xl:col-span-7 space-y-4 text-left font-mono">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs font-mono">
                  <div className="space-y-1.5">
                    <span className="text-zinc-550 uppercase text-[9.5px]">Select Platform Context</span>
                    <div className="flex gap-1 bg-black p-1 rounded-xl border border-[#4c1d95]/30">
                      {["TikTok", "YouTube", "InstagramStyle", "LinkedIn"].map((platformOpt) => (
                        <button
                          key={platformOpt}
                          onClick={() => {
                            setActiveHookPlatform(platformOpt);
                            triggerToast(`Switched hook context to ${platformOpt}`, "info");
                          }}
                          className={`flex-1 py-1.5 text-[8.5px] font-bold uppercase rounded-lg transition-all cursor-pointer ${
                            activeHookPlatform === platformOpt
                              ? "bg-gradient-to-r from-pink-500 to-purple-600 text-black font-black"
                              : "text-zinc-400 hover:text-white"
                          }`}
                        >
                          {platformOpt === "InstagramStyle" ? "Reels" : platformOpt}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <span className="text-zinc-550 uppercase text-[9.5px]">Select Script Category</span>
                    <div className="flex gap-1 bg-black p-1 rounded-xl border border-[#4c1d95]/30">
                      {["Founder script", "Ad script", "Testimonial"].map((styleOpt) => (
                        <button
                          key={styleOpt}
                          onClick={() => {
                            setActiveScriptStyle(styleOpt);
                            triggerToast(`Script style aligned: ${styleOpt}`, "info");
                          }}
                          className={`flex-1 py-1.5 text-[8.5px] font-bold uppercase rounded-lg transition-all cursor-pointer ${
                            activeScriptStyle === styleOpt
                              ? "bg-[#00e5ff] text-black font-black"
                              : "text-zinc-400 hover:text-white"
                          }`}
                        >
                          {styleOpt.split(" ")[0]}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-zinc-400 uppercase font-bold text-[9px]">Custom Hook Core Concept / Prompt</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customHookPrompt}
                      onChange={(e) => setCustomHookPrompt(e.target.value)}
                      className="flex-1 bg-black/80 border border-[#4c1d95]/40 p-2.5 rounded-xl text-white focus:outline-none focus:border-purple-500 text-[11px]"
                    />
                    <button
                      onClick={handleGenerateHooksAndScripts}
                      disabled={hooksLoading}
                      className="px-4 py-2.5 bg-pink-500 hover:bg-pink-600 font-bold uppercase text-[9.5px] rounded-xl text-black cursor-pointer"
                    >
                      {hooksLoading ? "Working..." : "Derive"}
                    </button>
                  </div>
                </div>

                {/* Hook lists displaying mock data output */}
                <AnimatePresence>
                  {generatedHooksList.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="space-y-2.5"
                    >
                      <span className="text-[10px] text-[#00e5ff] block uppercase tracking-wider font-bold">★ TARGET HOOKS READY ON CHANNELS (Engagement Probability Map)</span>
                      <div className="space-y-2">
                        {generatedHooksList.map((hk, index) => (
                          <div key={index} className="p-3 bg-black/90 border border-white/5 rounded-2xl flex justify-between items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <span className="text-[8.5px] text-zinc-500 block uppercase font-bold">Concept Angle: {hk.reason}</span>
                              <p className="text-zinc-200 text-xs mt-0.5 leading-relaxed font-sans">{hk.hook}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <span className="text-[8.5px] text-zinc-500 block uppercase">ENGAGEMENT SCORE</span>
                              <span className="text-pink-400 font-bold block text-sm font-mono">{hk.score}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* REAL-TIME CREATIVE PREVIEW PANE (5/12 parts) */}
              <div className="xl:col-span-5 p-4 rounded-2xl bg-[#08010d]/95 border border-pink-500/20 flex flex-col justify-between text-left relative overflow-hidden">
                <div className="absolute top-0 right-0 py-1.5 px-3 bg-pink-500/10 text-pink-400 font-mono text-[8px] uppercase tracking-wider rounded-bl-xl border-l border-b border-white/5 font-bold">
                  Hook Placement View
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-1.5 pb-2 border-b border-white/5">
                    <span className="text-pink-400 font-mono text-[10px] font-bold block uppercase tracking-wide">
                      ⚡ REAL-TIME INSTANT FEED HOOK
                    </span>
                  </div>

                  {/* Micro-feed preview card for Hook concept */}
                  <div className="p-3 bg-black/80 rounded-xl border border-pink-500/10 space-y-2 font-sans">
                    <span className="text-[8px] font-mono text-zinc-500 uppercase tracking-wide block">
                      FEED TYPE: {activeHookPlatform === "InstagramStyle" ? "REELS IN-FEED" : `${activeHookPlatform.toUpperCase()} CONTENT FEED`}
                    </span>

                    <div className="rounded-lg p-2.5 bg-gradient-to-br from-pink-950/20 to-[#0e041d] border border-pink-500/15 relative overflow-hidden">
                      {/* Large display typography representing viral hook statement overlay */}
                      <span className="text-[#00e5ff] font-mono text-[7px] uppercase font-black tracking-widest block mb-1">🔥 VIRAL STATEMENT</span>
                      <p className="text-white font-extrabold text-[12px] uppercase leading-tight font-sans tracking-wide">
                        "{customHookPrompt || "Why manual hiring is costing you 35% productivity."}"
                      </p>
                      
                      <div className="mt-2.5 flex items-center justify-between text-[7.5px] font-mono text-pink-400 font-bold bg-pink-500/5 p-1 rounded-md">
                        <span>EST. ENGAGEMENT POTENTIAL</span>
                        <span className="text-white text-[8.5px]">94.6% CALIBRATED</span>
                      </div>
                    </div>

                    <div className="text-[8.5px] font-mono text-[#00e5ff] leading-normal uppercase">
                      Category align: <strong className="text-white">{activeScriptStyle}</strong>
                    </div>
                  </div>
                </div>

                <div className="text-[7.5px] font-mono text-zinc-500 mt-2 border-t border-white/5 pt-1.5 flex justify-between items-center bg-black/10 p-1 rounded-lg">
                  <span>VELOCITY VIRALITY SLA</span>
                  <span className="text-[#00e5ff] font-semibold uppercase">{activeHookPlatform} SYNCED</span>
                </div>
              </div>

            </div>
          </div>

          {/* SECTION 4: AD CREATIVE GENERATOR */}
          <div className="p-6 rounded-3xl bg-black/60 backdrop-blur-xl border border-purple-500/15 shadow-xl space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-white/5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
                <h3 className="text-sm font-mono font-black text-white uppercase tracking-wider">
                  4. Ad Creative Generator
                </h3>
              </div>
              <span className="text-[8px] font-mono bg-yellow-950 px-2 py-0.5 rounded text-yellow-300">MULTIFORMAT</span>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
              
              {/* Form Input Controllers (7/12 parts) */}
              <div className="xl:col-span-7 space-y-4 text-left font-mono">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
                  <div className="space-y-1.5 col-span-2 md:col-span-1">
                    <label className="text-zinc-400 uppercase font-bold text-[9px]">Select Creative Layout Format</label>
                    <select
                      value={creativeFormat}
                      onChange={(e) => {
                        setCreativeFormat(e.target.value);
                        triggerToast(`Format preset selected: ${e.target.value}`, "info");
                      }}
                      className="w-full bg-black/80 border border-yellow-500/10 p-2.5 rounded-xl text-zinc-300 focus:outline-none focus:border-yellow-500 text-[11px] cursor-pointer"
                    >
                      <option>Video Ad (9:16 vertical)</option>
                      <option>Static Ad Frame (1:1 square)</option>
                      <option>Carousel Slides (1080x1080)</option>
                      <option>Story Graphic Ad (9:16)</option>
                      <option>YouTube Cinematic Thumbnail (16:9)</option>
                      <option>LinkedIn Corporate Header Banner</option>
                      <option>NexoraX Blog Hero Visual</option>
                    </select>
                  </div>

                  <div className="flex items-end col-span-2 md:col-span-1">
                    <button
                      onClick={handleGenerateCreative}
                      disabled={creativeLoading}
                      className="w-full py-2.5 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold uppercase tracking-widest rounded-xl text-[10px] cursor-pointer"
                    >
                      {creativeLoading ? "Structuring frame overlay..." : "Generate Creative Element"}
                    </button>
                  </div>
                </div>

                {generatedCreative && (
                  <div className="p-4 bg-gradient-to-br from-zinc-950 to-black rounded-2xl border border-yellow-500/20 space-y-3 font-mono text-xs">
                    <div className="flex justify-between items-center">
                      <span className="text-yellow-400 uppercase text-[9.5px] font-bold">★ COMPILED MULTI-LAYER RESOLUTION</span>
                      <span className="text-zinc-500">{generatedCreative.aspectRatio} Aspect Grid</span>
                    </div>

                    <div className="relative rounded-xl overflow-hidden aspect-video bg-zinc-900 border border-white/5 flex flex-col justify-end p-4">
                      <img
                        src={generatedCreative.imagePath}
                        alt="Compiled Layer"
                        className="absolute inset-0 w-full h-full object-cover opacity-50"
                        referrerPolicy="no-referrer"
                      />
                      {/* Branding elements simulation on top */}
                      <div className="relative z-10 space-y-1.5 text-left bg-black/40 p-3 rounded-lg backdrop-blur-sm border border-purple-500/20 max-w-sm">
                        <span className="text-purple-400 font-bold block text-[8px] uppercase tracking-wider">{generatedCreative.overlayElements.logoText}</span>
                        <p className="text-white font-extrabold text-[12px] leading-tight">{generatedCreative.overlayElements.headerText}</p>
                        <span className="text-[#00e5ff] font-bold block text-[9.5px] mt-0.5">{generatedCreative.overlayElements.leadCta}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* REAL-TIME CREATIVE PREVIEW PANE (5/12 parts) */}
              <div className="xl:col-span-5 p-4 rounded-2xl bg-zinc-950 border border-yellow-500/20 flex flex-col justify-between text-left relative overflow-hidden">
                <div className="absolute top-0 right-0 py-1.5 px-3 bg-yellow-500/10 text-yellow-500 font-mono text-[8px] uppercase tracking-wider rounded-bl-xl border-l border-b border-white/5 font-bold">
                  Frame Layout Pre-render
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-1.5 pb-2 border-b border-white/5">
                    <span className="text-yellow-500 font-mono text-[10px] font-bold block uppercase tracking-wide">
                      ⚡ REAL-TIME INSTANT FEED FRAME
                    </span>
                  </div>

                  {/* Real-time Dynamic Aspect Ratio frame pre-rendering simulation */}
                  <div className="p-2 bg-black rounded-xl border border-white/5 space-y-2">
                    <span className="text-[7.5px] font-mono text-zinc-500 uppercase tracking-wider block">
                      ASPECT COMPILER: {creativeFormat.includes("1:1") ? "SQUARE 1:1 GRID" : creativeFormat.includes("Header") ? "NARROW 3:1 STRIP" : "CINEMA 16:9 ASPECT"}
                    </span>

                    <div className={`relative bg-zinc-900 rounded-lg overflow-hidden border border-yellow-500/30 flex flex-col justify-end p-3 ${
                      creativeFormat.includes("vertical") || creativeFormat.includes("Story") 
                        ? "aspect-[9/12] h-48 mx-auto" 
                        : creativeFormat.includes("1:1") || creativeFormat.includes("Carousel")
                        ? "aspect-square h-40 mx-auto"
                        : creativeFormat.includes("Header")
                        ? "aspect-[4/1] w-full"
                        : "aspect-video w-full"
                    }`}>
                      <img 
                        src="https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=400&q=85"
                        alt="Background Art Preview"
                        className="absolute inset-0 w-full h-full object-cover opacity-30"
                        referrerPolicy="no-referrer"
                      />
                      
                      {/* Live typography layer overlaying in real-time */}
                      <div className="relative z-10 bg-black/60 p-2 rounded border border-white/5 backdrop-blur-sm self-start max-w-full">
                        <span className="text-purple-400 font-mono text-[6px] uppercase font-bold block">
                          {activeBrandName}
                        </span>
                        <p className="text-white font-mono font-extrabold text-[8.5px] leading-tight uppercase truncate">
                          Deploy {productName || "NexoraX AI"}
                        </p>
                        <span className="text-cyan-400 font-mono text-[7px] block font-bold capitalize mt-0.5">
                          {campaignCta || "Bypass queues"}
                        </span>
                      </div>
                    </div>

                    <div className="text-[7.5px] text-yellow-500 font-mono text-right font-bold uppercase block">
                      ✓ REAL-TIME OVERLAY STRUCTURING EFFECTIVELY ACTIVE
                    </div>
                  </div>
                </div>

                <div className="text-[7.5px] font-mono text-zinc-500 mt-2 border-t border-white/5 pt-1.5 flex justify-between items-center bg-black/10 p-1 rounded-lg">
                  <span>METADATA RESOLUTION SLA</span>
                  <span className="text-yellow-500 font-semibold uppercase">{creativeFormat.split(" ")[0]} SYNCED</span>
                </div>
              </div>

            </div>
          </div>

          {/* SECTION 5: A/B VARIATION LAB */}
          <div className="p-6 rounded-3xl bg-black/60 backdrop-blur-xl border border-purple-500/15 shadow-xl space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-white/5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <h3 className="text-sm font-mono font-black text-white uppercase tracking-wider">
                  5. A/B Variation Lab
                </h3>
              </div>
              <span className="text-[8px] font-mono bg-emerald-950 px-2 py-0.5 rounded text-emerald-300">MONTE CARLO</span>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
              
              {/* Form Input Controllers (7/12 parts) */}
              <div className="xl:col-span-7 space-y-4 text-left font-mono">
                <p className="text-xs text-zinc-400 leading-normal font-sans">
                  Test multiple permutations instantly. Generates up to 10 split copies for hooks, thumbnails, CTAs, captions, and visual styles to pinpoint premium performance ratios.
                </p>

                <button
                  onClick={handleGenerateABVariations}
                  disabled={variationLoading}
                  className="w-full py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 text-black font-semibold uppercase tracking-widest text-xs rounded-xl cursor-pointer"
                >
                  {variationLoading ? "Running Monte Carlo Sweep..." : "Generate 10 Dynamic Variations"}
                </button>

                {generatedVariations && (
                  <div className="space-y-4 text-xs font-mono">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Hooks Block */}
                      <div className="bg-black/80 rounded-2xl p-3 border border-white/5 space-y-2">
                        <span className="text-emerald-400 font-bold uppercase text-[9px] block">★ 10 Hook Permutations</span>
                        <div className="space-y-2">
                          {generatedVariations.hooks.map((item: any, i: number) => (
                            <div key={i} className="p-2 bg-zinc-950 border border-white/5 rounded-lg flex justify-between items-center gap-2 text-[10px]">
                              <span className="text-zinc-350 truncate">{item.text}</span>
                              <span className="text-emerald-400 font-bold">{item.ctr} CTR</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Thumbnail Block */}
                      <div className="bg-black/80 rounded-2xl p-3 border border-white/5 space-y-2">
                        <span className="text-[#00e5ff] font-bold uppercase text-[9px] block">★ 10 Thumbnail Overlays</span>
                        <div className="space-y-2">
                          {generatedVariations.thumbnails.map((item: any, i: number) => (
                            <div key={i} className="p-2 bg-zinc-950 border border-white/5 rounded-lg flex justify-between items-center gap-2 text-[10px]">
                              <span className="text-zinc-350 truncate">{item.text}</span>
                              <span className="text-emerald-400 font-bold">{item.ctr} CTR</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Additional KPI matrix visualizer for confidence level */}
                    <div className="p-3 bg-[#030805] border border-emerald-500/20 rounded-2xl flex justify-between items-center text-[10.5px]">
                      <span className="text-emerald-400 block font-bold">✓ SPLIT CALIBRATION SCORE</span>
                      <div className="flex gap-4">
                        <span>Conv Confidence: <strong className="text-white">98.4%</strong></span>
                        <span>Lead Potential: <strong className="text-[#00e5ff]">Uncompromised</strong></span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* REAL-TIME CREATIVE PREVIEW PANE (5/12 parts) */}
              <div className="xl:col-span-5 p-4 rounded-2xl bg-[#010804]/95 border border-emerald-500/20 flex flex-col justify-between text-left relative overflow-hidden">
                <div className="absolute top-0 right-0 py-1.5 px-3 bg-emerald-500/10 text-emerald-400 font-mono text-[8px] uppercase tracking-wider rounded-bl-xl border-l border-b border-white/5 font-bold">
                  A/B Calibration Core
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-1.5 pb-2 border-b border-white/5">
                    <span className="text-emerald-400 font-mono text-[10px] font-bold block uppercase tracking-wide">
                      ⚡ REAL-TIME INSTANT A/B PERMUTATION
                    </span>
                  </div>

                  {/* Real-time comparison split mock preview */}
                  <div className="space-y-2.5 font-sans">
                    <span className="text-[7.5px] font-mono text-zinc-500 uppercase tracking-wider block">
                      DYNAMIC CONFIDENCE RANGE
                    </span>

                    {/* Permutation A Mock */}
                    <div className="p-2 bg-black/80 rounded-xl border border-white/5 space-y-1">
                      <div className="flex justify-between items-center text-[8.5px] font-mono text-zinc-400">
                        <span className="font-bold text-white uppercase">📌 PERMUTATION A (Baseline)</span>
                        <span className="text-zinc-500 font-black">CTR: 4.8%</span>
                      </div>
                      <p className="text-[10px] text-zinc-300 font-sans italic">
                        "{productName || "NexoraX AI"} for {targetAudience || "your agency"}"
                      </p>
                      <div className="w-full bg-white/5 h-1 rounded-full overflow-hidden">
                        <div className="bg-zinc-500 h-full" style={{ width: "48%" }} />
                      </div>
                    </div>

                    {/* Permutation B Mock */}
                    <div className="p-2 bg-[#021c0b]/40 rounded-xl border border-emerald-500/20 space-y-1 relative overflow-hidden">
                      <div className="absolute -top-1 -right-1 py-0.5 px-1 bg-emerald-500/10 rounded-bl font-mono text-[5px] text-[#00ff66] uppercase font-bold">
                        WINNER
                      </div>

                      <div className="flex justify-between items-center text-[8.5px] font-mono text-[#00ff66]">
                        <span className="font-bold uppercase">⚡ PERMUTATION B (Calibrated)</span>
                        <span className="text-[#00ff66] font-black">CTR: 12.1%</span>
                      </div>
                      <p className="text-[10px] text-zinc-200 font-sans italic font-bold">
                        "Unlock Resource £150,000 resources immediately with {productName || "NexoraX AI"}"
                      </p>
                      <div className="w-full bg-white/5 h-1 rounded-full overflow-hidden">
                        <div className="bg-emerald-500 h-full" style={{ width: "94%" }} />
                      </div>
                    </div>

                    <div className="text-[7.5px] text-[#00ff66] font-mono text-right uppercase block">
                      ✓ Confidence optimization ratio: +152% uplift
                    </div>
                  </div>
                </div>

                <div className="text-[7.5px] font-mono text-zinc-500 mt-2 border-t border-white/5 pt-1.5 flex justify-between items-center bg-black/10 p-1 rounded-lg">
                  <span>98% CONFIDENCE RATIO</span>
                  <span className="text-emerald-400 font-semibold uppercase">MONTE CARLO READY</span>
                </div>
              </div>

            </div>
          </div>

          {/* SECTION 6: PRODUCT SHOT STUDIO */}
          <div className="p-6 rounded-3xl bg-black/60 backdrop-blur-xl border border-purple-500/15 shadow-xl space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-white/5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#a855f7] animate-pulse" />
                <h3 className="text-sm font-mono font-black text-white uppercase tracking-wider">
                  6. Product Shot Studio
                </h3>
              </div>
              <span className="text-[8px] font-mono bg-purple-950 px-2 py-0.5 rounded text-purple-300">SCENE STYLER</span>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
              
              {/* Form Input Controllers (7/12 parts) */}
              <div className="xl:col-span-7 space-y-4 text-left font-mono">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
                  <div className="space-y-1.5 col-span-2 md:col-span-1">
                    <label className="text-zinc-400 uppercase font-bold text-[9px]">Select Mockup Setting Style</label>
                    <select
                      value={productShotStyle}
                      onChange={(e) => {
                        setProductShotStyle(e.target.value);
                        triggerToast(`Style updated: ${e.target.value}`, "info");
                      }}
                      className="w-full bg-black/80 border border-purple-500/10 p-2.5 rounded-xl text-zinc-300 focus:outline-none focus:border-purple-500 text-[11px] cursor-pointer"
                    >
                      <option>Purple Glass branded scene</option>
                      <option>Futuristic Obsidian server room mockup</option>
                      <option>Website desktop interface mockup</option>
                      <option>Mobile application layout showcase</option>
                      <option>Premium corporate desk glass showcase</option>
                    </select>
                  </div>

                  <div className="flex items-end col-span-2 md:col-span-1">
                    <button
                      onClick={handleGenerateProductShot}
                      disabled={shotLoading}
                      className="w-full py-2.5 bg-[#4c1d95] hover:bg-purple-900 border border-purple-500/40 text-white font-semibold uppercase tracking-wider text-[10px] rounded-xl cursor-pointer"
                    >
                      {shotLoading ? "Compiling product scene..." : "Synthesize Product Scene"}
                    </button>
                  </div>
                </div>

                {generatedShots && (
                  <div className="bg-zinc-950 rounded-2xl p-4 border border-purple-500/25 space-y-3 font-mono text-xs">
                    <span className="text-purple-400 font-bold block text-[9.5px]">PRODUCT MOCKUP PREVIEW LAYER</span>
                    <div className="rounded-xl overflow-hidden aspect-video relative max-h-56">
                      <img
                        src={generatedShots.imageUrl}
                        alt="Mockup Frame"
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent flex items-end p-3">
                        <span className="text-[#00e5ff] font-bold text-[10px] uppercase">{generatedShots.title}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* REAL-TIME CREATIVE PREVIEW PANE (5/12 parts) */}
              <div className="xl:col-span-5 p-4 rounded-2xl bg-[#090212]/95 border border-purple-500/20 flex flex-col justify-between text-left relative overflow-hidden">
                <div className="absolute top-0 right-0 py-1.5 px-3 bg-purple-500/10 text-purple-400 font-mono text-[8px] uppercase tracking-wider rounded-bl-xl border-l border-b border-white/5 font-bold">
                  Isometric Glass Studio
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-1.5 pb-2 border-b border-white/5">
                    <span className="text-purple-400 font-mono text-[10px] font-bold block uppercase tracking-wide">
                      ⚡ REAL-TIME INSTANT MOCKUP
                    </span>
                  </div>

                  {/* Real-time scene simulation overlay */}
                  <div className="p-2 bg-black rounded-xl border border-white/5 space-y-2 font-sans">
                    <span className="text-[7.5px] font-mono text-zinc-500 uppercase tracking-wider block">
                      STUDIO RENDER: {productShotStyle.toUpperCase()}
                    </span>

                    <div className="relative aspect-video bg-zinc-900 rounded-lg overflow-hidden border border-purple-500/30 flex flex-col justify-end p-2.5">
                      <img 
                        src={
                          productShotStyle.includes("Obsidian")
                            ? "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=400&q=85"
                            : productShotStyle.includes("Website")
                            ? "https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?auto=format&fit=crop&w=400&q=85"
                            : productShotStyle.includes("Mobile")
                            ? "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=400&q=85"
                            : "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=400&q=85"
                        }
                        alt="Product Scene Real-render"
                        className="absolute inset-0 w-full h-full object-cover opacity-35"
                        referrerPolicy="no-referrer"
                      />

                      {/* Branding overlay inside mockup scene */}
                      <div className="relative z-10 self-center text-center p-3 rounded-xl bg-black/60 backdrop-blur-md border border-purple-500/25 max-w-[90%]">
                        <span className="text-purple-400 font-mono text-[6px] tracking-widest font-black uppercase block">
                          {activeBrandName || "NexoraX"}
                        </span>
                        <h4 className="text-white font-mono text-[10px] font-black tracking-wide uppercase mt-0.5">
                          {productName || "NexoraX AI"}
                        </h4>
                        <div className="mt-1 bg-white/5 rounded px-1.5 py-0.5 text-[5px] inline-block text-zinc-400 font-mono uppercase">
                          PREVIEW RENDERING IN PROGRESS
                        </div>
                      </div>
                    </div>

                    <div className="text-[7px] text-purple-400 font-mono text-right font-bold uppercase block">
                      ✓ GLASS LAYER RENDER ALIGNED
                    </div>
                  </div>
                </div>

                <div className="text-[7.5px] font-mono text-zinc-500 mt-2 border-t border-white/5 pt-1.5 flex justify-between items-center bg-black/10 p-1 rounded-lg">
                  <span>ISOMETRIC COMPILER SLA</span>
                  <span className="text-purple-400 font-semibold uppercase">{productShotStyle.split(" ")[0]} SYNCED</span>
                </div>
              </div>

            </div>
          </div>

          {/* SECTION 7: MULTI-LANGUAGE CAMPAIGN ENGINE */}
          <div className="p-6 rounded-3xl bg-black/60 backdrop-blur-xl border border-purple-500/15 shadow-xl space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-white/5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                <h3 className="text-sm font-mono font-black text-white uppercase tracking-wider">
                  7. Multi-Language Campaign Engine
                </h3>
              </div>
              <span className="text-[8px] font-mono bg-amber-950 px-2 py-0.5 rounded text-amber-300">COSMOPOLITAN</span>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
              
              {/* Form Input Controllers (7/12 parts) */}
              <div className="xl:col-span-7 space-y-4 text-left font-mono">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
                  <div className="space-y-1.5 col-span-2 md:col-span-1">
                    <label className="text-zinc-400 uppercase font-bold text-[9px]">Select Target Localisation Language</label>
                    <select
                      value={selectedLanguage}
                      onChange={(e) => {
                        setSelectedLanguage(e.target.value);
                        triggerToast(`Localisation switched to ${e.target.value}`, "info");
                      }}
                      className="w-full bg-black/80 border border-amber-500/10 p-2.5 rounded-xl text-zinc-300 focus:outline-none focus:border-amber-500 text-[11px] cursor-pointer"
                    >
                      <option>English</option>
                      <option>Arabic</option>
                      <option>Urdu</option>
                      <option>Hindi</option>
                      <option>Spanish</option>
                      <option>French</option>
                      <option>German</option>
                    </select>
                  </div>

                  <div className="flex items-end col-span-2 md:col-span-1">
                    <button
                      onClick={handleGenerateMultiLanguage}
                      disabled={langLoading}
                      className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold uppercase tracking-wider text-[10px] rounded-xl cursor-pointer"
                    >
                      {langLoading ? "Translating scripts..." : "Localize Campaign Target"}
                    </button>
                  </div>
                </div>

                {generatedTranslation && (
                  <div className="p-4 rounded-xl bg-[#030206] border border-amber-500/25 space-y-2.5 font-mono text-xs">
                    <div className="flex justify-between items-center text-[10px]">
                      <span className="text-amber-400 font-bold block uppercase">✓ LOCALIZED TRANSLATION MATRIX</span>
                      <span className="text-zinc-500">Language: {selectedLanguage}</span>
                    </div>

                    <div className="bg-black p-3 rounded-lg border border-white/5 space-y-2 leading-relaxed text-zinc-300">
                      <p><strong className="text-zinc-500 font-normal lowercase block text-[8px]">LOCAL SCRIPT TEXT:</strong> {generatedTranslation.script}</p>
                      <p className="border-t border-white/5 pt-1.5"><strong className="text-zinc-500 font-normal lowercase block text-[8px]">CAPTIONS LAYER:</strong> {generatedTranslation.captions}</p>
                      <p className="border-t border-white/5 pt-1.5"><strong className="text-zinc-500 font-normal lowercase block text-[8px]">SYNTHESIZED VOICE TRACK:</strong> <span className="text-yellow-400 font-semibold">{generatedTranslation.voicePreset}</span></p>
                    </div>
                  </div>
                )}
              </div>

              {/* REAL-TIME CREATIVE PREVIEW PANE (5/12 parts) */}
              <div className="xl:col-span-5 p-4 rounded-2xl bg-[#0d0701]/95 border border-amber-500/20 flex flex-col justify-between text-left relative overflow-hidden">
                <div className="absolute top-0 right-0 py-1.5 px-3 bg-amber-500/10 text-amber-400 font-mono text-[8px] uppercase tracking-wider rounded-bl-xl border-l border-b border-white/5 font-bold">
                  Cosmopolitan Grid
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-1.5 pb-2 border-b border-white/5">
                    <span className="text-amber-400 font-mono text-[10px] font-bold block uppercase tracking-wide">
                      ⚡ REAL-TIME INSTANT TRANSLATION
                    </span>
                  </div>

                  {/* Real-time subtitle simulation overlay based on chosen language */}
                  <div className="p-3 bg-black/80 rounded-xl border border-amber-500/10 space-y-2 font-sans">
                    <span className="text-[10px] font-mono text-zinc-500 uppercase block font-bold">
                      CURRENT LOCALE: {selectedLanguage.toUpperCase()}
                    </span>

                    <div className="p-2.5 rounded-lg bg-gradient-to-br from-amber-950/20 to-zinc-950 border border-amber-500/15 space-y-2 text-center">
                      <span className="text-amber-400 font-mono text-[7px] uppercase font-black tracking-widest block text-left">
                        LIVE OVERLAY SUBTITLE
                      </span>
                      
                      {/* Dynamic Arabic/Urdu/other translation string precomputed in real-time */}
                      <p className="text-white font-extrabold text-sm tracking-wide leading-relaxed font-sans">
                        {selectedLanguage === "Arabic" 
                          ? "نكسوراكس - نظام الذكاء الاصطناعي السيادي بقيمة 5.1 مليون جنيه إسترليني"
                          : selectedLanguage === "Urdu"
                          ? "نیکسوراکس - 5.1 ملین پاؤنڈ مالیت کا خود مختار مصنوعی ذہانت نظام"
                          : selectedLanguage === "French"
                          ? "NexoraX - Système de souveraineté d'infrastructure IA de £150,000"
                          : selectedLanguage === "Spanish"
                          ? "NexoraX - Sistema de soberanía de infraestructura de IA de £150,000"
                          : selectedLanguage === "German"
                          ? "NexoraX - 150K £ Souveränes KI-Infrastruktursystem"
                          : `NexoraX - £150,000 NexoraX AI Infrastructure Deployment`}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-[7.5px] font-mono text-zinc-500 mt-2 border-t border-white/5 pt-1.5 flex justify-between items-center bg-black/10 p-1 rounded-lg">
                  <span>COSMOPOLITAN TRANSLATION SLA</span>
                  <span className="text-amber-400 font-semibold uppercase">{selectedLanguage} ACTIVE</span>
                </div>
              </div>

            </div>
          </div>

          {/* SECTION 8: PUBLISHING PIPELINE */}
          <div className="p-6 rounded-3xl bg-black/60 backdrop-blur-xl border border-purple-500/15 shadow-xl space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-white/5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#00e5ff] animate-pulse" />
                <h3 className="text-sm font-mono font-black text-white uppercase tracking-wider">
                  8. Publishing Pipeline
                </h3>
              </div>
              <span className="text-[8px] font-mono bg-cyan-950 px-2 py-0.5 rounded text-cyan-300">DISPATCH CONTROL</span>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
              
              {/* Form Input Controllers (7/12 parts) */}
              <div className="xl:col-span-7 space-y-4 text-left font-mono">
                <p className="text-xs text-zinc-400 leading-normal font-sans">
                  Deploy fully certified campaigns directly onto social endpoints and database matrices on-grid without latency delays.
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 font-mono text-[10.5px]">
                  {[
                    "Social Media Automation",
                    "YouTube Cinematic",
                    "TikTok Direct",
                    "Instagram Reels",
                    "Facebook Ad Manager",
                    "LinkedIn Campaign Node",
                    "X Platform",
                    "Imperial Blog Engine",
                    "Global Ads Library"
                  ].map((channel) => {
                    const isSelected = selectedChannels.includes(channel);
                    return (
                      <button
                        key={channel}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedChannels(prev => prev.filter(c => c !== channel));
                          } else {
                            setSelectedChannels(prev => [...prev, channel]);
                          }
                          triggerToast(`Adjusted dispatch target: ${channel}`, "info");
                        }}
                        className={`p-2 rounded-xl text-left border transition-all cursor-pointer ${
                          isSelected
                            ? "bg-gradient-to-r from-purple-900/30 to-cyan-950/30 text-white border-[#00e5ff]/50"
                            : "bg-black/40 border-transparent text-zinc-500 hover:text-zinc-300 hover:border-white/5"
                        }`}
                      >
                        <span>{isSelected ? "✓ " : "○ "} {channel}</span>
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={handlePublishCampaign}
                  disabled={publishLoading}
                  className="w-full py-3 bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all hover:opacity-95 disabled:opacity-50 cursor-pointer shadow-lg"
                >
                  {publishLoading ? "Broadcasting campaign arrays..." : `Publish to ${selectedChannels.length} Channels`}
                </button>
              </div>

              {/* REAL-TIME CREATIVE PREVIEW PANE (5/12 parts) */}
              <div className="xl:col-span-5 p-4 rounded-2xl bg-[#010a0d]/95 border border-cyan-500/20 flex flex-col justify-between text-left relative overflow-hidden">
                <div className="absolute top-0 right-0 py-1.5 px-3 bg-cyan-500/10 text-cyan-400 font-mono text-[8px] uppercase tracking-wider rounded-bl-xl border-l border-b border-white/5 font-bold">
                  Dispatch View
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-1.5 pb-2 border-b border-white/5">
                    <span className="text-cyan-400 font-mono text-[10px] font-bold block uppercase tracking-wide">
                      ⚡ PRE-DISPATCH FLIGHT CHECK
                    </span>
                  </div>

                  {/* Pre-flight checklist based selectively on user states */}
                  <div className="p-3 bg-black/80 rounded-xl border border-cyan-500/10 space-y-1.5 font-mono text-[10.5px]">
                    <div className="flex justify-between items-center text-[#00ff66]">
                      <span>✓ AUDIT TARGET OK</span>
                      <span>100% ONLINE</span>
                    </div>

                    <p className="text-[10px] text-zinc-400 font-sans leading-normal">
                      Brand Anchor: <strong className="text-white">{activeBrandName || "NexoraX"}</strong> <br />
                      Primary Asset: <strong className="text-[#00e5ff]">{productName || "NexoraX System"}</strong>
                    </p>

                    <div className="border-t border-white/5 pt-1.5 mt-1.5 text-[9px] text-zinc-400 space-y-1">
                      <p className="text-zinc-500 font-black uppercase text-[8px]">ACTIVE DISPATCH SELECTED PIPES</p>
                      {selectedChannels.length === 0 ? (
                        <p className="text-red-400 font-bold italic animate-pulse">NO STREAMS DEPLOYED - PLEASE SELECT ABOVE</p>
                      ) : (
                        <div className="flex flex-wrap gap-1 leading-none py-1">
                          {selectedChannels.map(ch => (
                            <span key={ch} className="bg-cyan-950 text-cyan-300 font-bold px-1.5 py-0.5 rounded text-[8px]">
                              {ch.split(" ")[0]}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-[7.5px] font-mono text-zinc-500 mt-2 border-t border-white/5 pt-1.5 flex justify-between items-center bg-black/10 p-1 rounded-lg">
                  <span>DISPATCH CHANNELS READY</span>
                  <span className="text-cyan-400 font-semibold uppercase">{selectedChannels.length} ENDPOINTS</span>
                </div>
              </div>

            </div>
          </div>

          {/* SECTION 9: PERFORMANCE LEARNING */}
          <div className="p-6 rounded-3xl bg-black/60 backdrop-blur-xl border border-purple-500/15 shadow-xl space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-white/5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <h3 className="text-sm font-mono font-black text-white uppercase tracking-wider">
                  9. Performance Learning
                </h3>
              </div>
              <span className="text-[8px] font-mono bg-emerald-950 px-2 py-0.5 rounded text-emerald-300">ACTIVE FEEDBACK</span>
            </div>

            <p className="text-xs text-zinc-400 leading-normal font-sans">
              The NexoraX learning engine tracks campaign response cycles recursively to pinpoint exact conversion factors:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs font-mono">
              <div className="bg-[#030805] p-3 rounded-2xl border border-emerald-500/10 space-y-2">
                <span className="text-emerald-400 text-[10px] uppercase font-bold block">★ Conversion Champions</span>
                <div className="space-y-1.5 text-[11px] text-zinc-300 font-sans">
                  <p><strong className="font-mono text-[9px] text-zinc-500 block uppercase">Best Performing Hook:</strong> {bestHook}</p>
                  <p className="border-t border-white/5 pt-1.5 mt-1.5"><strong className="font-mono text-[9px] text-zinc-500 block uppercase">Best Lead Avatar:</strong> {bestAvatar}</p>
                  <p className="border-t border-white/5 pt-1.5 mt-1.5"><strong className="font-mono text-[9px] text-zinc-500 block uppercase">Dominant Ad Destination:</strong> {bestDestination}</p>
                  <p className="border-t border-white/5 pt-1.5 mt-1.5"><strong className="font-mono text-[9px] text-zinc-500 block uppercase">Best Video Length:</strong> {bestVideoLength}</p>
                </div>
              </div>

              <div className="bg-[#030805] p-3 rounded-2xl border border-emerald-500/10 space-y-2">
                <span className="text-[#00e5ff] text-[10px] uppercase font-bold block">★ Demographic Champions</span>
                <div className="space-y-1.5 text-[11px] text-zinc-300 font-sans">
                  <p><strong className="font-mono text-[9px] text-zinc-500 block uppercase">Best Call to Action:</strong> {bestCta}</p>
                  <p className="border-t border-white/5 pt-1.5 mt-1.5"><strong className="font-mono text-[9px] text-zinc-500 block uppercase">Winning Language:</strong> {bestLanguage}</p>
                  <p className="border-t border-white/5 pt-1.5 mt-1.5"><strong className="font-mono text-[9px] text-zinc-500 block uppercase">Target Audience Cohort:</strong> {bestAudience}</p>
                  <p className="border-t border-white/5 pt-1.5 mt-1.5"><strong className="font-mono text-[9px] text-zinc-500 block uppercase">Woven Visual Accent:</strong> {bestVisual}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TELEMETRY LOGS & OVERVIEW (RIGHT 1/3) */}
        <div className="space-y-6">
          {/* LIVE MULTI-FORMAT CAMPAIGN PREVIEW MONITOR & QUICK EXPORT HUB */}
          <div className="bg-[#0b0318]/95 border-2 border-purple-500/30 p-5 rounded-3xl space-y-4 shadow-[0_0_25px_rgba(168,85,247,0.15)] backdrop-blur-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 py-2.5 px-4 bg-purple-500/10 text-purple-300 font-mono text-[8.5px] uppercase font-black tracking-widest border-l border-b border-purple-500/25 rounded-bl-xl">
              Preview Dynamic HD
            </div>
            
            <div className="flex items-center gap-2 pb-1.5 border-b border-white/5">
              <span className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
              <h4 className="text-xs font-mono font-black text-white uppercase tracking-wider">
                Live Campaign Preview Monitor
              </h4>
            </div>

            {/* Platform Selection for Preview */}
            <div className="flex gap-1 bg-black p-1 rounded-xl border border-white/5 text-[9px] font-mono">
              {["LinkedIn", "TikTok Video", "YouTube Frame"].map((prevPlatform) => (
                <button
                  key={prevPlatform}
                  onClick={() => {
                    setPreviewPlatform(prevPlatform);
                    triggerToast(`Switched Live Monitor to ${prevPlatform}`, "info");
                    addLog(`MONITOR: Dynamic preview mapped to ${prevPlatform.toUpperCase()} layout.`);
                  }}
                  className={`flex-1 py-1 text-center uppercase rounded-lg transition-all cursor-pointer font-bold ${
                    previewPlatform === prevPlatform
                      ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-black"
                      : "text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {prevPlatform}
                </button>
              ))}
            </div>

            {/* The Live Rendering Canvas card */}
            <div className="p-4 bg-black rounded-2xl border border-white/5 space-y-3 shrink-0 relative">
              {previewPlatform === "LinkedIn" ? (
                // LinkedIn Feed Card Render
                <div className="space-y-2 text-left font-sans text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-[10px] text-black font-extrabold font-mono">
                      NX
                    </div>
                    <div>
                      <span className="text-white font-black block text-[10.5px] uppercase tracking-wide">NexoraX Premium Systems</span>
                      <span className="text-zinc-500 text-[8.5px] block font-mono">NexoraX Sponsor (SC887095) • Promoted</span>
                    </div>
                  </div>
                  
                  <p className="text-zinc-200 text-[10.5px] leading-relaxed line-clamp-4">
                    {generatedCampaign?.bodyCopy || `To the Executive Board: Traditional Client Resource Workflow pipelines delay operations by 45+ days. ${productOffer}`}
                  </p>
                  
                  {/* Visual thumbnail frame representation */}
                  <div className="rounded-xl overflow-hidden aspect-video bg-zinc-900 border border-white/5 relative flex flex-col justify-end p-4">
                    <img
                      src={generatedCreative?.imagePath || generatedShots?.imageUrl || "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1200&q=80"}
                      alt="Campaign Frame"
                      className="absolute inset-0 w-full h-full object-cover opacity-60"
                      referrerPolicy="no-referrer"
                    />
                    <div className="relative z-10 p-2.5 bg-black/50 rounded-lg border border-purple-500/20 max-w-xs backdrop-blur-sm self-start">
                      <span className="text-purple-400 font-bold block text-[7px] font-mono uppercase tracking-wider">
                        {activeBrandName}
                      </span>
                      <p className="text-white font-black text-[9px] leading-tight uppercase font-mono">
                        {generatedCampaign?.headerCopy || `Zero-Cost ${productName}`}
                      </p>
                    </div>
                  </div>
                  
                  {/* Simulated Action Banner */}
                  <div className="bg-zinc-950 p-2 rounded-xl border border-white/5 flex justify-between items-center text-[9px] font-mono gap-1">
                    <div className="min-w-0">
                      <span className="text-zinc-500 uppercase block text-[7.5px]">CALL TO ACTION</span>
                      <span className="text-white font-bold tracking-wide truncate block">
                        {campaignCta || "Schedule Candidate Technical Interview"}
                      </span>
                    </div>
                    <button
                      onClick={() => triggerToast("Action Cleared! Leads flowing into Secure Payment Provider ledger.", "success")}
                      className="px-2.5 py-1 bg-gradient-to-r from-purple-500 to-cyan-500 text-black font-black uppercase rounded-lg text-[8.5px] cursor-pointer shrink-0"
                    >
                      Bypass
                    </button>
                  </div>
                </div>
              ) : previewPlatform === "TikTok Video" ? (
                // TikTok Vertical Video Overlay
                <div className="relative aspect-[9/16] bg-zinc-950 rounded-2xl overflow-hidden flex flex-col justify-between p-3.5 h-80 mx-auto w-[180px] border border-cyan-500/20">
                  <img
                    src={generatedUgc?.videoUrl || "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=1200&auto=format&fit=crop"}
                    alt="TikTok Model"
                    className="absolute inset-0 w-full h-full object-cover opacity-50"
                    referrerPolicy="no-referrer"
                  />
                  
                  <div className="relative z-10 flex justify-between items-center text-[7px] font-mono text-cyan-400 bg-black/40 px-1.5 py-0.5 rounded">
                    <span>@nexoraxNexoraX</span>
                    <span className="font-extrabold">UGC REC</span>
                  </div>

                  <div className="relative z-10 space-y-1.5 self-end w-full text-left">
                    <p className="text-[8px] font-mono text-[#00e5ff] font-extrabold truncate">
                      {generatedHooksList.length > 0 ? generatedHooksList[0].hook : customHookPrompt}
                    </p>
                    <div className="bg-black/95 p-1.5 rounded text-[7px] font-mono text-zinc-355 leading-normal border border-white/5">
                      {generatedUgc?.captionsText || `[0:01] Direct billing fully anchored to NI NH******B under corporate sponsorship SC887095.`}
                    </div>
                    <button
                      onClick={() => triggerToast("Call initiated to Director's hotline: 07359******", "success")} 
                      className="w-full py-1 bg-pink-500 text-black font-black uppercase text-[7.5px] rounded-lg tracking-wider cursor-pointer"
                    >
                      Call 07359******
                    </button>
                  </div>
                </div>
              ) : (
                // YouTube Cinema Video ad frame
                <div className="space-y-2 text-left font-sans text-xs">
                  <div className="font-mono text-[8.5px] text-[#00e5ff] uppercase flex justify-between">
                    <span>16:9 PREROLL PREVIEW</span>
                    <span>Skip in 5s</span>
                  </div>
                  
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-zinc-950 border border-white/5 flex flex-col justify-between p-3">
                    <img
                      src={generatedCreative?.imagePath || "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1200&q=80"}
                      alt="YouTube Visual"
                      className="absolute inset-0 w-full h-full object-cover opacity-45"
                      referrerPolicy="no-referrer"
                    />
                    
                    <span className="relative z-10 text-white font-mono font-black text-[9.5px] uppercase tracking-wider drop-shadow-md bg-black/25 px-2 py-1 rounded inline-block self-start">
                      {generatedCampaign?.headerCopy || `Resource £150,000 NexoraX Transformation Platform`}
                    </span>
                    
                    <div className="relative z-10 flex justify-between items-center gap-4 bg-black/85 p-2 rounded-lg border border-purple-500/20">
                      <div className="min-w-0">
                        <span className="text-[7px] font-mono text-yellow-400 block uppercase font-bold">SPONSOR MEMORANDUM</span>
                        <p className="text-[9px] font-sans font-medium text-zinc-300 leading-tight truncate">
                          {productName}
                        </p>
                      </div>
                      <button
                        onClick={() => triggerToast("Landing page redirected to Resource vault standard.", "success")}
                        className="p-1 px-2 flex-shrink-0 bg-yellow-500 text-black font-bold uppercase text-[7.5px] font-mono rounded cursor-pointer"
                      >
                        Visit Site
                      </button>
                    </div>
                  </div>
                  <span className="text-[8.5px] font-mono text-zinc-500 italic block text-right">✓ Audio track voice synthesized under London RP RP Frequency</span>
                </div>
              )}
            </div>

            {/* QUICK EXPORT TOOLBAR */}
            <div className="pt-2 border-t border-white/10 space-y-2">
              <span className="text-[8.5px] font-mono text-zinc-400 uppercase tracking-widest block font-bold">
                ✓ Campaign Quick Export Actions
              </span>
              
              <div className="grid grid-cols-2 gap-2 text-[8.5px] font-mono">
                <button
                  onClick={() => {
                    const doc = {
                      product_name: productName,
                      offer_details: productOffer,
                      audience: targetAudience,
                      budget: budgetRange,
                      campaign_goal: campaignGoal,
                      platform_selected: platform,
                      cta: campaignCta,
                      timestamp_generated: new Date().toISOString(),
                      campaign_assets: {
                        caption: generatedCampaign?.bodyCopy,
                        header: generatedCampaign?.headerCopy,
                        hashtags: generatedCampaign?.suggestedHashtags,
                        ugc_actor: ugcAvatar,
                        ugc_narrative: ugcStyle,
                        ugc_captions: generatedUgc?.captionsText,
                        multi_language_localized: generatedTranslation ? {
                          lang: selectedLanguage,
                          script: generatedTranslation.script,
                          captions: generatedTranslation.captions,
                          voice: generatedTranslation.voicePreset
                        } : null
                      },
                      NexoraX_parameters: {
                        sort_code: "04-**-**",
                        bank: "Secure Payment Provider",
                        core_resource: "£150,000",
                        "Secure Payment Provider_sponsorship": "NH******B Linked",
                        corporate_license: "SC887095"
                      }
                    };
                    
                    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `nexorax_campaign_${productName.toLowerCase().replace(/\s+/g, '_')}_assets.json`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    
                    triggerToast("Campaign JSON Bundle downloaded!", "success");
                    addLog("EXPORTER: Dispatched compiled campaign asset JSON manifest package.");
                  }}
                  className="flex items-center justify-center gap-1 py-2 bg-gradient-to-r from-purple-900 to-purple-950 text-white font-bold uppercase rounded-xl hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer border border-[#c084fc]/30"
                >
                  <Download size={11} />
                  <span>Export JSON Hub</span>
                </button>
                
                <button
                  onClick={() => {
                    const textContent = `
=== NEXORAX NEXORAX AD CAMPAIGN ASSETS ===
Product: ${productName}
Audience: ${targetAudience}
Budget: ${budgetRange}
Platform: ${platform}
CTA: ${campaignCta}

--- GENERATED COPYWRITING ---
Header: ${generatedCampaign?.headerCopy || `Direct Institutional Mandate: Zero-Cost ${productName} Deployment`}
Body Ad Text: ${generatedCampaign?.bodyCopy || `The Director of NexoraX Digital Ltd (SC887595) presents a direct billing model under Services Guide...`}
Hashtags: ${(generatedCampaign?.suggestedHashtags || []).join(", ") || "#NexoraX #Client Resource WorkflowStrike #AccessToWork #AIOps"}

--- UGC VIDEO PRESETS ---
Actor Avatar: ${ugcAvatar}
Narrative Style: ${ugcStyle}
Captions Playback Text: ${generatedUgc?.captionsText || `[0:01] Direct billing fully anchored to NI NH******B under corporate sponsorship SC887595.`}

--- MULTI-LANGUAGE PRESET ---
Localised Selected Language: ${selectedLanguage}
Localised Script: ${generatedTranslation?.script || "No Translated script generated"}
Localised Sound Voiceover Profile: ${generatedTranslation?.voicePreset || "No Voice preset generated"}

--- INSTITUTIONAL BILLING LEDGER ACCREDITATIONS ---
Accounting Link: Secure Payment Provider Sort Code 04-**-**
Sponsorship License: NexoraX Digital Ltd (SC887095)
NexoraX Target NI Grant: NH******B
                    `;
                    navigator.clipboard.writeText(textContent.trim());
                    triggerToast("Full Ad copywriting copied to clipboard!", "success");
                    addLog("EXPORTER: Copied full copywriting bundle content to global terminal clipboard.");
                  }}
                  className="flex items-center justify-center gap-1 py-2 bg-gradient-to-r from-cyan-900 to-cyan-950 text-white font-bold uppercase rounded-xl hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer border border-[#38bdf8]/30"
                >
                  <FileText size={11} />
                  <span>Copy Copywriting</span>
                </button>
              </div>
            </div>
          </div>

          {/* CAMPAIGN METRICS SCHEDULER VIEW */}
          <div className="bg-[#0c051a]/90 border border-purple-500/20 p-5 rounded-3xl space-y-4">
            <span className="text-[10px] font-mono text-[#00e5ff] uppercase tracking-widest block font-black">
              ★ Campaign Calibration Specs
            </span>

            <div className="space-y-3.5 text-xs font-mono">
              <div className="flex justify-between border-b border-white/5 pb-2">
                <span className="text-zinc-500 uppercase font-bold">Sort code</span>
                <span className="text-white font-extrabold">04-**-** Secure Payment Provider</span>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-2">
                <span className="text-zinc-500 uppercase font-bold">Resource resource</span>
                <span className="text-[#00e5ff] font-extrabold">£5,100,000 Level</span>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-2">
                <span className="text-zinc-500 uppercase font-bold">Secure Payment Provider Grant NI</span>
                <span className="text-yellow-400 font-extrabold">NH******B Linked</span>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-2">
                <span className="text-zinc-500 uppercase font-bold">Perpetual Levy</span>
                <span className="text-purple-400 font-extrabold">1% Settlement allocation</span>
              </div>
              <p className="text-[10px] text-zinc-500 leading-normal font-sans">
                All lead results are routed into SecurePayment Business Ledger (Sort: 04-**-** | Account: ******15). Verification is automatically captured and recorded into Google Drive NexoraX Resource Library.
              </p>
            </div>
          </div>

          {/* REALTIME DIAGNOSTIC TELEMETRY LOGGER */}
          <div className="bg-black/90 border border-purple-500/20 rounded-3xl p-5 space-y-4 relative overflow-hidden shadow-xl">
            <div className="absolute top-0 right-0 p-3">
              <span className="h-2 w-2 rounded-full col-span-1 bg-emerald-400 animate-ping inline-block" />
            </div>

            <div className="flex items-center justify-between pb-2 border-b border-white/5">
              <span className="text-[10px] font-mono uppercase tracking-widest text-purple-400 block font-black">
                Telemetry Log stream
              </span>
              <button
                onClick={() => {
                  setTelemetryLogs([`[${new Date().toLocaleTimeString()}] LOGGER: Flushed output history buffer.`]);
                  triggerToast("Telemetry console flushed.", "info");
                }}
                className="text-[8px] font-mono text-zinc-500 hover:text-white uppercase transition-all"
              >
                Flush
              </button>
            </div>

            <div className="h-48 md:h-64 overflow-y-auto font-mono text-[9px] text-[#00e5ff] space-y-1.5 scrollbar-thin scrollbar-thumb-purple-950">
              {telemetryLogs.map((log, index) => (
                <div key={index} className="leading-normal hover:bg-white/5 p-1 rounded transition-all">
                  {log}
                </div>
              ))}
            </div>
          </div>

          {/* ACTIVE DISPATCHER CHANNELS VISUAL */}
          <div className="bg-gradient-to-br from-[#12072b]/80 to-black/90 border border-purple-500/15 p-5 rounded-3xl space-y-3.5">
            <span className="text-[10.5px] font-mono text-[#00e5ff] uppercase tracking-widest block font-bold">
              ✓ Automated Disseminator Grid
            </span>
            <div className="space-y-2 text-[10.5px] font-mono">
              <div className="flex items-center gap-2 text-emerald-400 font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block animate-pulse" />
                <span>Active campaigns synced on SecurePayment billing node</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-350">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00e5ff] inline-block" />
                <span>Multi-agent interstitial bypass active</span>
              </div>
              <p className="text-[9.5px] text-zinc-550 leading-relaxed font-sans pt-1 border-t border-white/5">
                Each campaign assets package is tested against deep neural constraints before deployment. Security triggers remain active.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM CTA CARD */}
      <div className="relative p-8 rounded-3xl bg-gradient-to-r from-purple-900/40 via-pink-900/30 to-cyan-950/40 border border-purple-500/20 text-center space-y-4 overflow-hidden z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(168,85,247,0.1),transparent_70%)] pointer-events-none -z-10" />
        <span className="text-[9px] font-mono bg-[#00e5ff]/20 text-[#00e5ff] px-3 py-1 rounded-full border border-[#00e5ff]/35 uppercase tracking-widest font-black inline-block">
          NEXORAX HARVEST ENGINE ACTIVATED
        </span>
        <h3 className="text-xl md:text-3xl font-sans font-black text-white tracking-tight leading-tight uppercase max-w-xl mx-auto">
          Create Ads That Sell. Generate. Test. Publish. Scale.
        </h3>
        <p className="text-zinc-400 text-xs md:text-sm max-w-lg mx-auto font-sans leading-relaxed">
          Lock campaigns inside Secure Payment Provider Sort 04-**-** account pipelines. Empower VIP Private Secretaries with automated systems that bypass administration barriers instantly.
        </p>

        <div className="pt-2 flex justify-center gap-3">
          <button
            onClick={() => {
              triggerToast("Synchronizing whole system matrices to Resource standard...", "success");
              addLog("SYSTEM: Full synchronization sequence completed. All 9 modules locked.");
            }}
            className="px-6 py-2.5 bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl hover:scale-105 active:scale-95 transition-all cursor-pointer shadow-lg"
          >
            Deploy Omni-Channel Matrix
          </button>
        </div>
      </div>
    </div>
  );
}
